import React, { useState, useRef, useEffect, useMemo, useCallback, lazy, Suspense } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Drawer, useMediaQuery, useTheme, IconButton, Typography, Paper, SwipeableDrawer, Snackbar, Alert, Zoom, Dialog } from '@mui/material';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import '../styles/EngagingMap.css';
import CloseIcon from '@mui/icons-material/Close';
import { DEFAULT_MOCK_USERS } from '../constants/data';
import UserSidebar from '../components/UserSidebar';
import { getBestAvailableLocation, watchUserLocation, getLocationByIP } from '../services/LocationService';
import { canPublishAndQuery } from '../services/locationPolicy';
import { generateMockUsers } from '../services/MockDataGenerator';
import { FEATURE_FLAGS } from '../../utils/featureFlags';
import { useUser } from '../../users/providers/UserProvider';
import { getNearbyUsers, updateUserLocation, getLocationPrivacy } from '../../users/service/userApi';
import { getWishlistMatches } from '../../wishlist/services/wishlistAPI';
import { listSafeSpotsForMap } from "../../users/service/safeSpotApi";
import { getMyTradeRequests } from "../../trades/tradeApi";
import SafeSpotSheet from "../../safeSpots/SafeSpotSheet";
import { distanceBetween, spotLngLat } from "../../safeSpots/safeSpotDistance";
import MarketplaceView from '../components/MarketplaceView';
import { announceTradeMatches } from '../../trades/tradeMatchSignal';
import NewMatchesToast from '../components/NewMatchesToast';
import Loader from '../../styles/Loader';
import { topChromeReservedPx } from '../../layout/components/TopAccountBar';
import { Z, CHROME_INSET } from '../../styles/zLayers';
import { dvh } from '../../styles/viewportHeight';
import ROUTES from '../../router/routesModel';
import { SAFE_TOP, SAFE_BOTTOM } from '../safeArea';
import { shouldShowNearbyPill } from '../nearbyCollectors';
import {
  opportunityCalloutCopy,
  matchDirection,
  buildRevealViewport,
  isWishlistMatchUser,
  pinMatchBannerCopy,
  idStr,
} from '../tradeOpportunity';
import {
  animatedTargets,
  ctaLabelFor,
  rankMatches,
  sessionStatusCopy,
  MAX_ANIMATED_TRAILS,
} from '../matchRanking';
import { fitBoundsFor } from '../matchLines';
import useRevealSession from '../useRevealSession';
import { useDismissible } from '../../native/dismissibleStack';
import { canOpenAppSettings, openAppSettings } from '../../native/appSettings';
import { readDraft } from '../../native/drafts';
import useAppResume from '../../native/useAppResume';
import MatchDeck, { DECK_CARD_W, DECK_CARD_H } from '../components/MatchDeck';
import MatchDockTransition from '../components/MatchDockTransition';
// MOTION_EASING rather than a hardcoded cubic-bezier: this file used to inline
// the same curve the token already defines.
import { MOTION, MOTION_EASING } from '../../styles/motionTokens';
import { prefersReducedMotion } from '../../styles/particleField';
import { trackEvent } from '../../analytics/analytics';
import {
  fetchFirstTradeMission,
  emptyMapReason,
  DISCOVERY_OK,
  DISCOVERY_RATE_LIMITED,
  DISCOVERY_FAILED,
  DISCOVERY_OUT_OF_AREA,
} from '../../layout/components/firstTradeMission';
import { REVOKED_SESSION_CODES } from '../../utils/apiErrorUtils';
import { MAP_UNAVAILABLE_MESSAGE, MAP_UNAVAILABLE_TITLE, MAP_OFFLINE_MESSAGE } from '../mapCopy';
import { needsLocationWrite, noteLocationWritten, recentDiscovery, rememberDiscovery, discoveryTicket, recentHotspots, rememberHotspots } from '../discoveryVisitCache';
import MapDiscoveryHeader, { MapRoamingBar } from '../components/MapDiscoveryHeader';
import MapStatusSurface from '../components/MapStatusSurface';
import { getCollectorHotspots } from '../../users/service/userApi';
import { reverseGeocode } from '../services/placeSearch';
import { useRoamingEnabled } from '../../utils/useRuntimeFlags';
import { getDiscoveryRadiusMeters } from '../../utils/runtimeFlags';
import NearbyCollectorsStatus from '../components/NearbyCollectorsStatus';
import MapUtilityControls from '../components/MapUtilityControls';
import CollectorPreviewSheet from '../components/CollectorPreviewSheet';
import { nearbyCollectorCount } from '../mapMarkers';
import { collectorFirstName, generatedAvatarUrl } from '../collectorIdentity';
import { getUserCollection } from '../../collection/collectionApi';
import { getOrCreateThread } from '../../chat/chatApi';
import { claimRefusals } from '../../utils/refusalNotice';

// mapbox-gl/react-map-gl are heavy and only needed once the map itself
// mounts, not at module load — see #69.
const MapView = lazy(() => import('../components/MapView'));

// The map's dialogs and full-screen moments, split out of the main chunk to
// keep it under the CI bundle budget (512,000 B gzipped). None of them is on
// screen when the map first paints: each opens on a tap or after a match is
// found. The first four stay mounted while closed, exactly as before, so their
// code starts loading as soon as the map mounts and is already there when the
// user taps. The privacy panel only mounts inside its open Dialog, so its code
// is fetched on map mount below instead; either way it is loaded before it is
// needed, and keeps working if the network drops afterwards.
const TradeRequestModal = lazy(() => import('../../trades/TradeRequestModal'));
const TradeProposedMoment = lazy(() => import('../../trades/TradeProposedMoment'));
const MatchRevealMoment = lazy(() => import('../components/MatchRevealMoment'));
const BidModal = lazy(() => import('../../bids/BidModal'));
const loadLocationPrivacySettings = () => import('../components/LocationPrivacySettings');
const LocationPrivacySettings = lazy(loadLocationPrivacySettings);

/**
 * Safe-area terms, re-exported so the map keeps one import path for them.
 *
 * The definitions moved to ../safeArea because the map chrome components need
 * them too and importing from the page that renders those components would be
 * a cycle. #232 converted the rest of the app and scoped MapPage out; the call
 * sites it left behind now read the same chain as everything else.
 */
export { SAFE_TOP, SAFE_BOTTOM } from '../safeArea';


/**
 * Copy for the two location-watch outcomes (P2-48).
 *
 * Never `GeolocationPositionError.message`. That string is a provider
 * diagnostic written for whoever ships the browser — "Network location
 * provider at 'https://www.googleapis.com/' : No response received." — and it
 * used to be rendered verbatim, third-party hostname and all, on the app's
 * home screen. Same rule as MAP_UNAVAILABLE_MESSAGE: no provider name, no
 * error code, no host.
 */
export const LOCATION_WATCH_DEGRADED_MESSAGE =
  "We can't update your location right now — the map is still showing where you were last.";

/**
 * Shown only when the map has nothing: no position, so nothing to draw. This
 * is the one case where covering the map is honest.
 */
export const LOCATION_UNAVAILABLE_MESSAGE =
  "We couldn't find your location. Check that location is turned on for Tradeloft, then try again.";

/**
 * Errors that mean the map genuinely cannot render, after `load` has fired.
 *
 * Deliberately a small allow-list of initialisation/authorisation signatures —
 * a rejected or missing access token, a style that never loaded, a lost WebGL
 * context. Anything else that arrives once the map is on screen is treated as
 * transient, because the map the user is looking at is still rendering.
 */
const FATAL_MAP_ERROR = /access[- ]?token|unauthoriz|not authoriz|forbidden|webgl|context lost|load style|style load|failed to load style|style is not done loading/i;

/**
 * Should this map error blank the map behind "Map unavailable"? (P1-43)
 *
 * mapbox-gl has exactly one `error` channel and puts everything on it. GL JS's
 * `source_cache` fires an ErrorEvent for every tile failure whose status is not
 * 404, and sources are evented-parented up to the map; react-map-gl forwards
 * that channel unfiltered (`error` sits in its generic `otherEvents` table
 * alongside `data` and `sourcedata`). So a network blip while walking, a 429 or
 * a 5xx on one vector tile used to arrive here indistinguishable from "the map
 * is dead" — and the opaque error card then covered a map that was rendering
 * correctly, with nothing able to clear it in place.
 *
 * The load state is the thing that separates them:
 *
 * - Before `load`, the map has never painted a frame. Whatever went wrong IS
 *   the reason the user is staring at an empty box, so every error is fatal.
 * - After `load`, only a genuine initialisation/authorisation failure is fatal.
 *   A tile/source error (one that names its `sourceId`) is transient by
 *   definition — unless the tile CDN is rejecting our credentials outright,
 *   which means every subsequent tile fails too.
 *
 * Erring toward "transient" is the right direction: the cost of a wrong guess
 * is a logged error nobody sees, versus a working map replaced by a dead end.
 */
/**
 * How long the map may sit on "Loading map..." before the route calls it
 * unavailable. Generous on purpose: this is the backstop for a map that is
 * never arriving, not a performance budget, and `handleMapLoad` clears the
 * error if one turns up late anyway.
 */
/**
 * The centre of the area this beta serves, used only until a real position
 * arrives. Discovery refuses anything outside these bounds, so any other
 * default would be a map of somewhere the app cannot serve.
 */
const LAUNCH_AREA_CENTRE = { longitude: 34.7818, latitude: 32.0853 };

export const MAP_LOAD_TIMEOUT_MS = 15000;

export const isFatalMapError = (error, hasLoaded) => {
  if (!hasLoaded) return true;

  // mapbox-gl's ErrorEvent carries the real Error under `.error`; the HTTP
  // status lives on the AJAXError inside it.
  const cause = error?.error || error;
  const status = cause?.status ?? error?.status;

  if (error?.sourceId || error?.source || cause?.sourceId) {
    return status === 401 || status === 403;
  }

  return FATAL_MAP_ERROR.test(String(cause?.message || ''));
};

/**
 * Horizontal view chips — same slide pattern as Collection set filters.
 * "Listings" = nearby cards for sale on the map.
 * "Cards" = full catalog browse (/pokemontcg) — navigates away.
 * Top-slot occupants, most important first.
 *
 * The status pill, the roaming bar, the failure banner and the match-session
 * messages all used to be independently positioned and then nudged out of each
 * other's way with `overlayTopPushed` / `TOAST_PUSH_PX`. That produced a
 * shuffle: overlays stepped down when a toast arrived and back up when it left,
 * and two of them could still land on the same pixels.
 *
 * There is one slot now, and exactly one thing in it. Ranking them here — as
 * data — is what makes "only one can be present at a time" checkable instead of
 * being an emergent property of six independent render guards.
 */
const TOP_SLOT = {
  MATCH_TOAST: 'match_toast',
  SESSION: 'session',
  STATUS_BANNER: 'status_banner',
  ROAMING: 'roaming',
  OPPORTUNITY: 'opportunity',
  NEARBY: 'nearby',
  NONE: 'none',
};

/** Height of the top row the slot sits under, per safe-area. */
const TOP_ROW_PX = 54;

/**
 * Mock and fallback users carry ids like 'mock-3', which the collection
 * endpoint would reject. Checking the shape here keeps a dev fixture from
 * producing a 400 in the network tab on every pin tap.
 */
const isValidMongoId = (id) => typeof id === 'string' && /^[a-fA-F0-9]{24}$/.test(id);

/** Height of the failure banner plus its gap, when the count pill sits under it. */
const STATUS_BANNER_PUSH_PX = 92;

/** The compact preview's own height, so the utility stack can step above it. */
const PREVIEW_SHEET_PX = 302;

/**
 * Bounds around a cluster's members, in the [[w,s],[e,n]] shape fitBounds wants.
 *
 * Tapping a cluster fits the camera to the people in it rather than stepping to
 * a guessed zoom level: a fixed "+2 zoom" either overshoots a tight cluster or
 * leaves a spread-out one still clustered, and both read as the tap not working.
 */
const boundsForCluster = (cluster) => {
  const points = (cluster?.members || [])
    .map((m) => (Array.isArray(m.coordinates)
      ? { longitude: m.coordinates[0], latitude: m.coordinates[1] }
      : m.coordinates))
    .filter((p) => p && Number.isFinite(p.longitude) && Number.isFinite(p.latitude));
  if (points.length === 0) return null;
  const lngs = points.map((p) => p.longitude);
  const lats = points.map((p) => p.latitude);
  return [
    [Math.min(...lngs), Math.min(...lats)],
    [Math.max(...lngs), Math.max(...lats)],
  ];
};

/**
 * An element's position relative to an ancestor, by LAYOUT offset.
 *
 * Deliberately not `getBoundingClientRect`: that includes any transform on the
 * element or its ancestors, so an element measured while its container is
 * mid-entrance-animation reports where it is *right now* rather than where it
 * will settle. The dock flight has to aim at the settled position.
 */
const offsetWithin = (el, ancestor) => {
  let x = 0;
  let y = 0;
  let node = el;
  while (node && node !== ancestor && node.offsetParent) {
    x += node.offsetLeft;
    y += node.offsetTop;
    node = node.offsetParent;
  }
  return { x, y };
};

/**
 * Is this 403 the launch-area refusal, or something else wearing the same code?
 *
 * `auth` runs BEFORE the discovery handler on both routes and answers a
 * suspended or deleted account with its own 403. A bare `status === 403` branch
 * would therefore tell a suspended user they are standing outside the beta
 * launch area — trading one wrong sentence for a differently wrong one. The
 * revoked-session codes mean "this session is over", which the sign-out path
 * already handles; everything else at 403 on these two routes is the geofence,
 * which is the only permission the discovery endpoints check.
 */
const isOutOfAreaFailure = (failure) =>
  failure?.status === 403 && !REVOKED_SESSION_CODES.includes(failure?.code);

/**
 * One outcome for the whole discovery load (#82, #102), ranked by how permanent
 * the answer is.
 *
 * A launch-area refusal (403) wins outright: it is not a failure at all but a
 * deliberate answer, it cannot change on a retry, and both endpoints sit behind
 * the same geofence — so if either one refused, so did the other or so will it.
 * A 429 comes next, above a generic failure, because both endpoints share one
 * limiter and the throttle copy is then the more accurate story. Any failure at
 * all means the picture is incomplete and must not be presented as a
 * confirmed-empty neighbourhood.
 *
 * The 403-over-429 order matters because the two calls are sequential over one
 * budget: "nearby refused, then the budget ran out on matches" is reachable,
 * and the 429's "try again shortly" would be false for that user.
 */
/** Both searches failed with no HTTP answer at all: the server is out of reach (B-27). */
const noAnswerFrom = (a, b) => !!a && !a.status && !!b && !b.status;

const discoveryOutcomeFrom = (nearbyFailure, matchesFailure) => {
  if (isOutOfAreaFailure(nearbyFailure) || isOutOfAreaFailure(matchesFailure)) {
    return DISCOVERY_OUT_OF_AREA;
  }
  if (nearbyFailure?.status === 429 || matchesFailure?.status === 429) {
    return DISCOVERY_RATE_LIMITED;
  }
  if (nearbyFailure || matchesFailure) return DISCOVERY_FAILED;
  return DISCOVERY_OK;
};

/**
 * Second clause of the callout — the directions the headline is NOT about (P1-34).
 *
 * The headline is one direction's sentence with that direction's OWN count. Any
 * other direction present in the match set gets named here with its own count,
 * so every number in the callout is attached to the claim it can actually
 * support. Each string is a fragment, not a sentence: the headline already
 * carries the "Trade opportunity —" / "Perfect match —" label, and repeating it
 * per group reads as a stutter.
 *
 * These live here rather than in `tradeOpportunity.js` because that module's
 * exported copy is the *headline* vocabulary, addressed to a set that shares one
 * direction; this is the page's own way of joining several such sets. If a
 * second surface ever needs the same fragments, move them there together.
 */
const CALLOUT_TAIL_COPY = {
  mutual: (n) => `${n} more have a two-way swap with you`,
  they_want: (n) => `${n} more want cards from your Trade Binder`,
  they_have: (n) => `${n} more have cards you want`,
};

const MapPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  /**
   * The viewport width the top row lays itself out against.
   *
   * This used to be `window.innerWidth` read inline in the JSX — a snapshot
   * taken at whatever moment React happened to render, with nothing subscribed
   * to changes. A phone changes its width without remounting anything (rotate
   * it, unfold it, or let the on-screen keyboard resize the visual viewport),
   * so the segmented control kept whichever layout it mounted with: rotate a
   * 320px portrait phone to landscape and the row stayed in its icon-less
   * compact form, and only an unrelated re-render — a location fix landing —
   * would quietly correct it. `isMobile` above is reactive, so half the page
   * responded to a rotation and half did not.
   *
   * MatchDeck already does exactly this for its own narrow test; this is the
   * same subscription, at the page that owns the row.
   */
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 390
  );

  // Fetch the privacy panel's split code while the map settles, so opening it
  // later neither waits on nor needs the network. A failed fetch is left for
  // the panel's own render to retry and report.
  useEffect(() => {
    loadLocationPrivacySettings().catch(() => {});
  }, []);

  useEffect(() => {
    const check = () => setViewportWidth(window.innerWidth);
    check();
    window.addEventListener('resize', check);
    window.addEventListener('orientationchange', check);
    return () => {
      window.removeEventListener('resize', check);
      window.removeEventListener('orientationchange', check);
    };
  }, []);

  const navigate = useNavigate();
  // Carries a trade-list link's trade intent (docs/PUBLIC_TRADE_LIST.md).
  const routerLocation = useLocation();
  const { user: authUser } = useUser();
  
  const user = useMemo(() => {
    if (authUser) {
      return {
        id: authUser._id,
        name: `${authUser.name?.first || ''} ${authUser.name?.last || ''}`.trim() || 'You',
        email: authUser.email,
        avatar: authUser.image?.url || generatedAvatarUrl(authUser.name?.first || 'You'),
      };
    }
    return {
      id: 'local-user-123',
      name: 'Local User',
      email: 'user@example.com',
      avatar: generatedAvatarUrl('Local User'),
    };
  }, [authUser]);
  const mapRef = useRef();
  const mapContainerRef = useRef(null);
  const locationWatchIdRef = useRef(null);
  // Guards loadLocationAndUsers against firing its publish/discovery
  // sequence more than once concurrently — StrictMode's double-invoke and
  // any accidental duplicate mount effect otherwise both cost a real
  // location publish and two discovery calls. See #69.
  const locationLoadInFlightRef = useRef(false);

  // State for the sidebar and error handling
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedSpot, setSelectedSpot] = useState(null);
  const [spotTrades, setSpotTrades] = useState(null);
  const [spotTradesLoading, setSpotTradesLoading] = useState(false);
  const [spotTradesError, setSpotTradesError] = useState(false);
  // The compact collector preview — step one of the two-step preview. Separate
  // from `sidebarOpen` because they are different surfaces over the same
  // selection, and exactly one of them is up at a time.
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewCards, setPreviewCards] = useState([]);
  const [previewTotal, setPreviewTotal] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [messageBusy, setMessageBusy] = useState(false);
  const [recentering, setRecentering] = useState(false);
  /**
   * The preview sheet's measured height, published by the sheet itself.
   *
   * PREVIEW_SHEET_PX is the first-paint fallback now. The height depends on the
   * card strip, the trust line and whether a match row is present, so a constant
   * was always going to be wrong for some collector — and when it was, the
   * utility stack sat underneath the sheet, which is the exact bug
   * MapUtilityControls exists to prevent.
   */
  const [previewHeight, setPreviewHeight] = useState(PREVIEW_SHEET_PX);
  const [mapError, setMapError] = useState(null);
  const [serverUnreachable, setServerUnreachable] = useState(false);
  const [mapLoading, setMapLoading] = useState(true);
  // Has the map painted at least one frame? (P1-43) A ref, not state: `onError`
  // can fire in the same tick as `onLoad`, and this must be readable
  // synchronously rather than one render later. It resets with the page, which
  // is the right lifetime — but note it tracks OUR knowledge, not the map's
  // state: mapbox fires `load` once per instance and `reuseMaps` hands a
  // remount the same instance, so `onLoad` alone would leave this false over a
  // live map. MapView reconciles that at source (see its readiness effect) and
  // calls `onLoad` either way, so everything below can trust this again.
  const mapLoadedRef = useRef(false);
  const [allUsers, setAllUsers] = useState([]); // Start with empty array
  const [locationAlert, setLocationAlert] = useState(false);
  const [locationLoading, setLocationLoading] = useState(true);
  const [locationError, setLocationError] = useState(null);
  // True when we have no browser-granted location to act on — permission was
  // denied, or all we could resolve was an IP/default approximation. Distinct
  // from locationError: this is an honest, expected state, not a failure to retry.
  const [locationOff, setLocationOff] = useState(false);
  // B-44. Why it is off. True only for a refused permission, which in the app
  // only Settings > Apps > Tradeloft can undo. False for "no position" (the
  // phone's Location switched off, no fix indoors), which that page cannot fix.
  const [locationDenied, setLocationDenied] = useState(false);
  // P2-48 — the background watch stopped delivering fixes on a map that
  // already has a position and its data. Non-blocking on purpose: everything
  // under it is still correct, it just isn't moving any more.
  const [locationWatchDegraded, setLocationWatchDegraded] = useState(false);
  const [locationNoticeSeq, setLocationNoticeSeq] = useState(0);
  /**
   * Does the map have something real on screen? (P2-48)
   *
   * True once the initial load has resolved a position and finished its
   * discovery pass; false again the moment a fresh load starts. This is what
   * separates "a background tick failed" from "the map has nothing to draw",
   * and only the second of those earns a blocking card.
   *
   * A ref, not state: the watch's error callback is a closure created once
   * when the watch is registered, so it must read the CURRENT answer rather
   * than whatever was true at registration time.
   */
  const mapHasUsableDataRef = useRef(false);
  /**
   * Announce a degraded watch once per healthy stretch, re-armed by the next
   * successful fix. The watch's timeout is 60s, so without this a persistent
   * failure would re-raise the notice every minute for as long as the user
   * stayed on the map.
   */
  const watchDegradedAnnouncedRef = useRef(false);
  const [userLocation, setUserLocation] = useState(null);
  /**
   * Whether the page holds a real fix (one the browser reported) right now.
   * Read by the location watch's error callback, a closure registered once per
   * tracking toggle, so it cannot read `userLocation` itself. Set wherever a
   * position is stored, in the same tick, so a timeout that arrives before the
   * next render still sees it.
   */
  const hasRealFixRef = useRef(false);
  const [privacySaved, setPrivacySaved] = useState(false);
  // Vetted venues. Deliberately NOT merged into `allUsers`: a venue is not a
  // collector, and folding it in would inflate every count and every cluster
  // that is supposed to be about people.
  const [safeSpots, setSafeSpots] = useState([]);

  // Loaded once per session. A failure is silent on purpose: venues are
  // context, and a map that still shows every collector is not broken
  // because a supplementary list did not arrive.
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    listSafeSpotsForMap()
      .then((spots) => { if (!cancelled) setSafeSpots(Array.isArray(spots) ? spots : []); })
      .catch(() => { if (!cancelled) setSafeSpots([]); });
    return () => { cancelled = true; };
  }, [user]);
  const [privacySavedSeq, setPrivacySavedSeq] = useState(0);
  const [locationTrackingEnabled, setLocationTrackingEnabled] = useState(false);
  // A ref, not state: nothing renders from it, and a cleanup that closes over
  // the mount-time state value can never clear a timer armed later.
  const locationControlTimerRef = useRef(null);
  const foilSparkTimerRef = useRef(null);
  const recenterTimerRef = useRef(null);
  const [matchedUserIds, setMatchedUserIds] = useState([]);
  // Every match, normalized — including the ones with no publishable coordinate,
  // which never become map markers but are still real matches to browse.
  const [matchRecords, setMatchRecords] = useState([]);
  // Last announced count, so a refresh that returns the same matches doesn't
  // re-fire the celebration.
  const matchedCountRef = useRef(0);
  // The toast borrows the top slot for a few seconds; the ambient pills step
  // down so the corner never stacks two things on the same pixels.
  const [matchesToastUp, setMatchesToastUp] = useState(false);
  // Map | Listings is remembered on THIS history entry, so Back from a seller's
  // profile (or a chat opened from a listing) lands on Listings again instead of
  // the map — before, the remount reset it and the collector lost their place
  // (tablet QA, versionCode 11). A fresh Map-tab tap is a new entry and opens
  // the map. Router keys (usr/key/idx) are spread through untouched.
  const [viewMode, setViewMode] = useState(() => {
    const saved = typeof window !== 'undefined' ? window.history.state?.mapViewMode : null;
    return saved === 'marketplace' ? 'marketplace' : 'map';
  });
  useEffect(() => {
    if (typeof window === 'undefined' || window.history.state?.mapViewMode === viewMode) return;
    try {
      window.history.replaceState({ ...(window.history.state || {}), mapViewMode: viewMode }, '');
    } catch {
      // A history the page may not write to only costs the restore.
    }
  }, [viewMode]);
  const [tradeModalOpen, setTradeModalOpen] = useState(false);
  // Who the open arena is addressed to, set by whatever opened it. Not
  // `selectedUser`: a location revoke clears that on purpose (the map may no
  // longer name anyone near you), and an open arena - one restored from a
  // saved offer included - was left addressed to nobody: "Trader", their
  // binder unloadable, and Confirm answering "receiverId and at least one
  // offeredCard are required" (E2E audit 5·21). An offer to a collector you
  // already chose does not depend on where you are.
  const [tradeTarget, setTradeTarget] = useState(null);
  // The listing whose Trade button opened the arena, or null (B-58). Handed to
  // the arena as its requested card, so a tap on a card does not land on an
  // empty builder. Every other way in clears it.
  const [tradePrefill, setTradePrefill] = useState(null);
  // B-59: an offer the arena saved before Android closed the app, handed back
  // to it once. Cleared by every way out of the arena.
  const [tradeResume, setTradeResume] = useState(null);
  // The offer that just went out, or null. A SNAPSHOT rather than a boolean:
  // the send moment shows the actual cards, and the builder resets its own
  // state the instant `onSuccess` returns, so there is nothing left to read
  // back afterwards (see buildSendSummary in tradeSendSummary.js).
  const [tradeProposed, setTradeProposed] = useState(null);
  const [bidModalListing, setBidModalListing] = useState(null);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  // E2E audit E-02. The phone not locating you does not unpublish a point you
  // shared earlier, so "Location is off" must not claim "your position has not
  // been shared with other collectors" while they can still see you. Asked of
  // the server only while that card is up: true = still on the map, false =
  // not, null = unknown.
  const [accountPublished, setAccountPublished] = useState(null);
  // Asked again when the privacy panel closes, so choosing Hidden there is
  // reflected on this card at once instead of after a reload.
  useEffect(() => {
    if (!locationOff || privacyOpen) return undefined;
    let cancelled = false;
    Promise.resolve().then(() => getLocationPrivacy())
      .then((p) => { if (!cancelled) setAccountPublished(typeof p?.published === 'boolean' ? p.published : null); })
      .catch(() => { if (!cancelled) setAccountPublished(null); });
    return () => { cancelled = true; };
  }, [locationOff, privacyOpen]);
  const [opportunityCalloutDismissed, setOpportunityCalloutDismissed] = useState(false);
  const [foilSparkId, setFoilSparkId] = useState(null);
  const [matchReveal, setMatchReveal] = useState(null);
  // Mirrors matchReveal for SYNCHRONOUS reads (#148). A pin's Android
  // pointerdown/touchstart/click can land in the same tick that opens the reveal,
  // before React commits the state; the ref lets a marker handler see "a reveal
  // is taking over" immediately, instead of one render too late.
  const matchRevealRef = useRef(null);

  // ── Match trail session plumbing ──────────────────────────────────────────
  // The session hook is declared further down (it needs handlers defined there),
  // so callbacks that run above it reach it through this ref.
  const revealSessionRef = useRef(null);
  // Measured, never assumed: the camera reserves the deck's real height so the
  // framed match doesn't land behind it, and the flight lands on the real slot.
  const [deckHeight, setDeckHeight] = useState(0);
  const deckCardSlotRef = useRef(null);
  const activeBeaconRef = useRef(null);
  // Value-signature of the last camera fit, so an unchanged frame never re-fits
  // and cancels the user's own pan/zoom.
  const lastFitRef = useRef(null);
  // Camera to restore when a reveal is closed back to the deck (#85). Written
  // only by `handleRevealMatch`, read and unconditionally cleared by
  // `closeMatchReveal`.
  //
  // It CAN go stale: the location-revoked branch of the watch error handler
  // tears the reveal down directly (`matchRevealRef.current = null;
  // setMatchReveal(null)`) without going through `closeMatchReveal`, leaving a
  // populated stash with no reveal on screen. That is harmless only because the
  // same branch cancels the session, so `multi` is false at the next close and
  // the value is never read. Said explicitly because an earlier revision of this
  // comment claimed a stale value was impossible, which is not true.
  const preRevealViewportRef = useRef(null);
  // { from, to } rects for the card's flight between map and deck, in map
  // container coordinates. Null whenever no card is in the air.
  const [dockFlight, setDockFlight] = useState(null);

  // Why the map is empty, from real account state rather than a guess.
  const [mission, setMission] = useState(null);
  // Outcome of the last discovery load (#82). A failed or throttled request
  // must never render as a confirmed-empty neighbourhood, so the empty-state
  // copy needs to know whether the request actually succeeded.
  const [discoveryStatus, setDiscoveryStatus] = useState(DISCOVERY_OK);
  // The last discovery query, so a retry re-runs exactly the same load.
  const lastDiscoveryQueryRef = useRef(null);

  // Roaming — browse collectors in other cities
  const [roamingLocation, setRoamingLocation] = useState(null);
  const [cityQuery, setCityQuery] = useState('');
  const [cityResults, setCityResults] = useState([]);
  const [citySearchOpen, setCitySearchOpen] = useState(false);
  const citySearchTimerRef = useRef(null);
  
  // Handle map loading and errors
  const handleMapLoad = () => {
    mapLoadedRef.current = true;
    setMapLoading(false);
    setMapError(null);
  };
  
  const handleMapError = (error) => {
    // Diagnostics stay here, where we can read them. What went wrong with the
    // map provider is our problem to fix, not something a collector can act on
    // — and the previous copy named a build variable and asked the reader to
    // set it, which is an instruction only a developer could follow. A missing
    // token in a production or Play build would have shown that to every
    // tester on the app's home screen.
    console.error('Map error:', error);

    // P1-43 — a transient tile/source failure on a map that has already loaded
    // is not a reason to replace it with a dead end. See isFatalMapError.
    if (!isFatalMapError(error, mapLoadedRef.current)) return;

    setMapError(MAP_UNAVAILABLE_MESSAGE);
    setMapLoading(false);
  };
  
  // Retry loading the map if there was an error
  const handleRetryMap = () => {
    setMapError(null);
    // Only re-raise the veil for a map that has genuinely not arrived.
    //
    // `load` is one-shot per Mapbox instance, so raising "Loading map..." over
    // a map that already loaded puts up a veil nothing is left to lower — and
    // that is precisely how Retry turned a recoverable error into an
    // unbreakable loop: measured in the running app, Retry cleared the message,
    // left the veil up, re-armed the watchdog, and "Map unavailable" came back
    // ~15s later, twice over, with only a full page reload getting out.
    setMapLoading(!mapLoadedRef.current);
    // `loadLocationAndUsers` re-reads the viewer's fix, re-publishes it,
    // re-queries HOME discovery and recentres the camera on HOME. That IS
    // going home, so the roam has to end with it — otherwise the roaming bar
    // keeps announcing a city over a map that is already home and full of home
    // collectors, and offers "Back to my location" to somebody who is already
    // there. Clearing it here rather than inside `loadLocationAndUsers`,
    // because the roaming refresh path calls that too and must NOT end the roam.
    setRoamingLocation(null);
    // Retry asks the server again rather than reusing the last visit's
    // answers: it is how the card learns the server is back (B-27).
    loadLocationAndUsers(true);
  };

  /**
   * The map has to fail out loud, not sit on a spinner.
   *
   * `handleMapError` only runs for failures Mapbox reports through its own
   * `error` event. A map that never finishes and never reports anything —
   * a worker that throws while parsing, a WebGL context that cannot be
   * created, a style request that hangs behind a captive portal — left
   * `mapLoading` true forever, so the route showed "Loading map..." with no
   * error, no retry and no way out. Measured in the rig: a broken Mapbox
   * worker produced an uncaught ReferenceError, `onError` never fired, and the
   * veil stayed up indefinitely over a map that was never coming.
   *
   * A watchdog turns that dead end into the existing "Map unavailable" surface,
   * which already offers Try again. It is deliberately generous — this is a
   * last resort for a map that is not coming, not a performance budget — and it
   * is self-correcting: `handleMapLoad` clears `mapError`, so a map that
   * finally arrives after the deadline still replaces the message.
   */
  useEffect(() => {
    if (viewMode !== 'map' || !mapLoading || mapError) return undefined;
    const timer = window.setTimeout(() => {
      if (mapLoadedRef.current) return;
      setMapError(MAP_UNAVAILABLE_MESSAGE);
      setMapLoading(false);
    }, MAP_LOAD_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [viewMode, mapLoading, mapError]);


  // Where the camera sits before we know anything. This used to be Central
  // Park, which is nobody's location in an Israel-first beta: on a cold load
  // the map painted New York under the "Finding collectors near you" card,
  // which reads as a broken map rather than a loading one. It is covered
  // while the position resolves (see the backdrop below), and this value is
  // the fallback for the frames before that.
  const [viewport, setViewport] = useState({
    longitude: LAUNCH_AREA_CENTRE.longitude,
    latitude: LAUNCH_AREA_CENTRE.latitude,
    zoom: 12,
    bearing: 0,
    pitch: 0
  });

  // The committed camera, mirrored into a ref. `handleRevealMatch` needs the
  // pre-reveal framing without taking `viewport` as a dependency — it changes
  // on every frame of a pan, and re-creating the reveal handler that often
  // would churn every memoized child it is passed to.
  const viewportRef = useRef(viewport);
  useEffect(() => {
    viewportRef.current = viewport;
  }, [viewport]);

  /**
   * "Online" is a 15-minute window over `lastLogin`, which the server stamps on
   * every session it mints — password login and the Supabase bridge, and the
   * client re-runs that bridge on token refresh. So the window is now applied to
   * a value the server actually maintains, and it means "obtained a session in
   * the last 15 minutes", not "is looking at their phone".
   *
   * It under-claims by design: a collector who has been browsing for an hour
   * reads Offline between refreshes. That is the right direction to be wrong in
   * for an app that sends people to meet strangers in person.
   */
  const isOnline = (lastLogin) =>
    !!lastLogin && Date.now() - new Date(lastLogin).getTime() < 15 * 60 * 1000;

  /**
   * No `lastLogin` means we do not know when this collector was last here.
   *
   * This used to fall back to `new Date().toISOString()`, which invented "now"
   * for exactly the case where we knew nothing — every surface that formats
   * `lastActive` relatively then reported a total stranger as active seconds
   * ago. Null instead: an absent field produces no text.
   */
  const lastActiveFrom = (lastLogin) => lastLogin || null;

  const normalizeApiUser = (u) => ({
    id: u._id,
    username: `${u.name?.first || ''} ${u.name?.last || ''}`.trim() || 'Collector',
    profilePicture: u.image?.url || generatedAvatarUrl(u.name?.first || 'Collector'),
    image: u.image || null,
    coordinates: u.coordinates || [],
    // 'Approximate' is not a member of LOCATION_PRECISION, so it fell through
    // UserSidebar's icon switch to the red 'location off' glyph — the sheet
    // said a collector's location was withheld while their pin was on screen.
    // A fuzzed public point IS neighbourhood-grade. Pinned by
    // MapPage.locationPrecision.test.js for both normalizers.
    locationPrecision: 'Neighborhood',
    onlineStatus: isOnline(u.lastLogin) ? 'Online' : 'Offline',
    online: isOnline(u.lastLogin),
    lastActive: lastActiveFrom(u.lastLogin),
    bio: '',
    collectionStats: { totalCards: 0, cardsForSale: 0, completedTrades: 0 },
    cards: [],
    matchedCards: [],
    // The server sends both of these with every nearby collector and this
    // normalizer used to drop them, which is why the preview could never show a
    // verified tick or a trade count for anyone who wasn't also a match.
    ...matchTrustFields(u),
  });

  /**
   * Trust signals the match payload carries and the map needs.
   *
   * The server sends `verified`, `successfulTrades` and a rounded
   * `approxDistanceMeters` with every match, and both normalizers below used to
   * drop all three on the floor. That is why the reveal's distance line never
   * appeared in the app even though the component supports it: the field never
   * survived the trip from the response to the trader object. The Match Deck
   * needs exactly the same three to answer "is this person worth meeting", so
   * they are lifted once, here, and attached wherever a match is built.
   */
  const matchTrustFields = (m) => ({
    verified: !!m.verified,
    successfulTrades:
      typeof m.successfulTrades === 'number' ? m.successfulTrades : 0,
    // Rounded to 100 m by the server and derived from a coordinate already in
    // the same response — carrying it adds no precision the client didn't hold.
    approxDistanceMeters:
      typeof m.approxDistanceMeters === 'number' ? m.approxDistanceMeters : null,
  });

  // A wishlist match becomes its own map marker, carrying the specific cards of
  // MINE that this nearby trader has for trade/sale.
  const normalizeMatchUser = (m) => ({
    id: m.userId,
    username: `${m.name?.first || ''} ${m.name?.last || ''}`.trim() || 'Trader',
    profilePicture: m.image?.url || generatedAvatarUrl(m.name?.first || 'Trader'),
    coordinates: m.coordinates || [],
    // Same fix as `normalizeApiUser` above, which is where it was made and
    // where it stopped: 'Approximate' is not in LOCATION_PRECISION, so a
    // matched collector's drawer showed the red "location off" glyph while
    // their pin was on the map. A match's point is fuzzed exactly like a
    // nearby collector's, so it is the same 'Neighborhood'.
    locationPrecision: 'Neighborhood',
    onlineStatus: isOnline(m.lastLogin) ? 'Online' : 'Offline',
    online: isOnline(m.lastLogin),
    lastActive: lastActiveFrom(m.lastLogin),
    bio: '',
    collectionStats: { totalCards: 0, cardsForSale: 0, completedTrades: 0 },
    cards: [],
    matchedCards: m.matchedCards || [],
    theyWantCards: m.theyWantCards || [],
    direction: m.direction || "they_have",
    ...matchTrustFields(m),
  });

  // Merge wishlist matches into a base marker list: attach matchedCards to an
  // existing pin, or add the trader as a new pin so they always appear.
  const mergeMatches = (baseUsers, matches) => {
    const byId = new Map(baseUsers.map((u) => [String(u.id), u]));
    matches.forEach((m) => {
      const id = String(m.userId);
      if (byId.has(id)) {
        const existing = byId.get(id);
        existing.matchedCards = m.matchedCards || [];
        existing.theyWantCards = m.theyWantCards || [];
        existing.direction = m.direction || "they_have";
        Object.assign(existing, matchTrustFields(m));
      } else if (Array.isArray(m.coordinates) && m.coordinates.length >= 2) {
        byId.set(id, normalizeMatchUser(m));
      }
    });
    return Array.from(byId.values());
  };

  const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';

  const searchCities = useCallback(async (query) => {
    if (!query || query.length < 2 || !MAPBOX_TOKEN) {
      setCityResults([]);
      return;
    }
    try {
      const res = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&types=place,locality&limit=5`
      );
      const json = await res.json();
      setCityResults(
        (json.features || []).map((f) => ({
          name: f.place_name,
          longitude: f.center[0],
          latitude: f.center[1],
        }))
      );
    } catch {
      setCityResults([]);
    }
  }, [MAPBOX_TOKEN]);

  const handleCityQueryChange = useCallback((e) => {
    const q = e.target.value;
    setCityQuery(q);
    setCitySearchOpen(true);
    if (citySearchTimerRef.current) clearTimeout(citySearchTimerRef.current);
    citySearchTimerRef.current = setTimeout(() => searchCities(q), 300);
  }, [searchCities]);

  // `radius` defaults to whatever the SERVER says, read at call time. It used
  // to be a hardcoded 50000 here and at every other call site, and since
  // parseDiscoveryQuery only ever clamps DOWNWARD that literal — not the
  // deployment's ceiling — was the real reach of discovery. Raising
  // DISCOVERY_MAX_RADIUS_METERS had no effect while this said 50000.
  const loadDataAtCoordinates = useCallback(async (lng, lat, radius = getDiscoveryRadiusMeters()) => {
    lastDiscoveryQueryRef.current = { lng, lat, radius };
    const ticket = discoveryTicket();

    let realUsers = [];
    let nearbyFailure = null;
    let apiUsers;
    try {
      apiUsers = await getNearbyUsers(lng, lat, radius);
      if (Array.isArray(apiUsers) && apiUsers.length > 0) {
        realUsers = apiUsers
          .filter(u => String(u._id) !== String(user.id))
          .map(normalizeApiUser)
          .filter(u => Array.isArray(u.coordinates) && u.coordinates.length >= 2);
      }
    } catch (e) {
      nearbyFailure = e;
      console.warn('getNearbyUsers failed:', e?.message);
    }

    let matches = [];
    let matchesFailure = null;
    try {
      const res = await getWishlistMatches(lng, lat, radius);
      if (Array.isArray(res)) matches = res;
    } catch (e) {
      matchesFailure = e;
      console.warn('getWishlistMatches failed:', e?.message);
    }
    // Retry and Back to my location show fresh answers; the next visit from
    // here reuses those, not an older visit's (B-28).
    if (!nearbyFailure && !matchesFailure) {
      rememberDiscovery(ticket, user.id, lng, lat, radius, apiUsers, matches);
    }

    setDiscoveryStatus(discoveryOutcomeFrom(nearbyFailure, matchesFailure));
    setServerUnreachable(noAnswerFrom(nearbyFailure, matchesFailure));

    // A failed matches request is unknown, not zero — don't clear known
    // matches, announce a change, or count it as a real result (#82).
    if (!matchesFailure) {
      announceTradeMatches(matches.length, matchedCountRef.current);
      matchedCountRef.current = matches.length;
      setMatchedUserIds(matches.map((m) => m.userId));
      setMatchRecords(matches.map(normalizeMatchUser));
      setOpportunityCalloutDismissed(false);
      if (matches.length > 0) trackEvent('match_found', { count: matches.length });
    }

    if (!nearbyFailure || !matchesFailure) {
      setAllUsers(mergeMatches(realUsers, matches));
    }
    // Both failed: keep whatever was on the map; the discovery status carries
    // the honest explanation instead of a fabricated empty result.
  }, [user.id]);

  const handleSelectCity = useCallback(async (city) => {
    setRoamingLocation(city);
    setCityQuery('');
    setCityResults([]);
    setCitySearchOpen(false);

    setViewport({
      longitude: city.longitude,
      latitude: city.latitude,
      zoom: 12,
      bearing: 0,
      pitch: 0,
    });

    setLocationLoading(true);
    await loadDataAtCoordinates(city.longitude, city.latitude, 100000);
    setLocationLoading(false);
  }, [loadDataAtCoordinates]);

  const handleBackToMyLocation = useCallback(() => {
    setRoamingLocation(null);
    if (!userLocation?.coords) return;

    // The same gate the initial load runs, and for the same reason. An IP or
    // default-centre fix may be pointed AT but never asked ABOUT — see
    // locationPolicy: querying with one "fabricates nearby matches around a
    // point that may not be true". This path read `userLocation` with no check
    // at all, so a user whose fix the policy had already refused reached both
    // discovery requests around that point simply by roaming and pressing Back,
    // and the results flowed on into the Trades signal.
    if (!canPublishAndQuery(userLocation)) {
      setViewport({
        longitude: userLocation.coords.longitude,
        latitude: userLocation.coords.latitude,
        // The blocked zoom, matching the initial load: an approximate point
        // shown at street level reads as a precision we do not have.
        zoom: 12,
        bearing: 0,
        pitch: 0,
      });
      // Clearing is required, not tidiness. The roamed city's collectors are
      // answers about somewhere else; left in `allUsers` they render an
      // "N collectors nearby" pill directly over the card that says we cannot
      // show nearby collectors.
      setAllUsers([]);
      setMatchedUserIds([]);
      setMatchRecords([]);
      matchedCountRef.current = 0;
      return;
    }

    setViewport({
      longitude: userLocation.coords.longitude,
      latitude: userLocation.coords.latitude,
      zoom: 14,
      bearing: 0,
      pitch: 0,
    });
    loadDataAtCoordinates(
      userLocation.coords.longitude,
      userLocation.coords.latitude,
      getDiscoveryRadiusMeters()
    );
  }, [userLocation, loadDataAtCoordinates]);

  // Load user location and initialize map data. `fresh === true` skips the
  // remembered search; a click handler passes an event, which is not `true`.
  const loadLocationAndUsers = async (fresh) => {
    // A second call while one is already running (StrictMode's double-invoke
    // in dev, a rapid mount/remount, or a stray extra trigger) would publish
    // the location and query discovery twice. Collapse to one in-flight run.
    if (locationLoadInFlightRef.current) return;
    locationLoadInFlightRef.current = true;

    setLocationLoading(true);
    setLocationError(null);
    setLocationOff(false);
    setLocationDenied(false);
    // A reload has nothing on screen yet, so until it lands the watch is once
    // again allowed to report a hard failure (P2-48).
    mapHasUsableDataRef.current = false;
    setLocationWatchDegraded(false);
    watchDegradedAnnouncedRef.current = false;
    // A fresh load starts with no verdict; a failure below sets it honestly.
    setDiscoveryStatus(DISCOVERY_OK);

    try {
      const location = await getBestAvailableLocation();
      setUserLocation(location);
      hasRealFixRef.current = canPublishAndQuery(location);

      if (location && location.coords) {
        // Only a location the browser itself reported may be published or
        // used to query discovery — see locationPolicy.js. An IP or default
        // approximation is for centering the viewport only.
        if (canPublishAndQuery(location)) {
          const { longitude: lng, latitude: lat } = location.coords;
          const radius = getDiscoveryRadiusMeters();
          // The signed-in account's `_id` (see the `user` memo). The cache is
          // module state that outlives a sign-out, so it is keyed on this.
          const me = user.id;
          // B-28: every Map visit used to rewrite the location and search
          // again. Write only on a real move; reuse a search that is a few
          // minutes old from the same spot. See discoveryVisitCache.js.
          if (needsLocationWrite(me, lng, lat)) {
            try {
              const res = await updateUserLocation(lng, lat);
              // A 207 "partial" means the pin did not move, so try next visit.
              if (res?.status !== 'partial') noteLocationWritten(me, lng, lat);
            } catch (_) { /* non-critical */ }
          }

          lastDiscoveryQueryRef.current = { lng, lat, radius };
          const recent = fresh === true ? null : recentDiscovery(me, lng, lat, radius);
          const ticket = discoveryTicket();

          // Fetch real nearby users from API (exclude self by both _id and id fields)
          let realUsers = [];
          let nearbyFailure = null;
          let apiUsers;
          try {
            apiUsers = recent ? recent.users : await getNearbyUsers(lng, lat, radius);
            if (Array.isArray(apiUsers) && apiUsers.length > 0) {
              realUsers = apiUsers
                .filter(u => String(u._id) !== String(user.id))
                .map(normalizeApiUser)
                .filter(u => Array.isArray(u.coordinates) && u.coordinates.length >= 2);
            }
          } catch (e) {
            nearbyFailure = e;
            console.warn('getNearbyUsers failed:', e?.message);
          }

          // Fetch wishlist matches — nearby traders who have cards I want. This is
          // the core of the map: "who around me has cards I'd like?"
          let matches = [];
          let matchesFailure = null;
          try {
            const res = recent ? recent.matches : await getWishlistMatches(lng, lat, radius);
            if (Array.isArray(res)) matches = res;
          } catch (e) {
            matchesFailure = e;
            console.warn('getWishlistMatches failed:', e?.message);
          }
          // Only a search that fully answered is worth reusing.
          if (!recent && !nearbyFailure && !matchesFailure) {
            rememberDiscovery(ticket, me, lng, lat, radius, apiUsers, matches);
          }

          setDiscoveryStatus(discoveryOutcomeFrom(nearbyFailure, matchesFailure));
          // A reused answer says nothing about the server now. The mission
          // fetch below, made on every visit, speaks for it then (B-27).
          if (!recent) setServerUnreachable(noAnswerFrom(nearbyFailure, matchesFailure));

          // A failed matches request is unknown, not zero — don't clear known
          // matches, announce a change, or count it as a real result (#82).
          if (!matchesFailure) {
            // Announce before setState so the previous count is still readable —
            // only a genuine increase wakes the Trades icon and the toast.
            announceTradeMatches(matches.length, matchedCountRef.current);
            matchedCountRef.current = matches.length;

            setMatchedUserIds(matches.map((m) => m.userId));
            // Kept alongside the ids: this is the list the reveal session ranks,
            // and it has to include matches with no publishable coordinate,
            // which never become map markers.
            setMatchRecords(matches.map((m) => normalizeMatchUser(m)));
            setOpportunityCalloutDismissed(false);
            if (matches.length > 0) trackEvent('match_found', { count: matches.length });
          }

          if (realUsers.length > 0) {
            setAllUsers(mergeMatches(realUsers, matches));
          } else if (FEATURE_FLAGS.ENABLE_MOCK_USERS) {
            // Dev-only fallback. ENABLE_MOCK_USERS is forced false in production builds.
            try {
              const mockUsers = await generateMockUsers(location, { count: 10, radius: 1500 });
              const base = (Array.isArray(mockUsers) && mockUsers.length > 0) ? mockUsers : DEFAULT_MOCK_USERS;
              setAllUsers(mergeMatches(base, matches));
            } catch (e) {
              console.warn('generateMockUsers failed:', e?.message);
              setAllUsers(mergeMatches(DEFAULT_MOCK_USERS, matches));
            }
          } else if (!nearbyFailure || !matchesFailure) {
            // Production / mock-disabled: still show wishlist-match pins if any.
            setAllUsers(mergeMatches([], matches));
          }
          // Both requests failed: keep the map as it was; the discovery
          // status carries the honest explanation, not a fabricated empty.
        } else {
          // No browser-granted location: nothing to publish, nothing to query
          // with — a fabricated match around an IP/default point is worse
          // than none. Render the honest "location off" state instead.
          setLocationOff(true);
          setMatchedUserIds([]);
          setMatchRecords([]);
          matchedCountRef.current = 0;
          setAllUsers([]);
        }

        setViewport({
          longitude: location.coords.longitude,
          latitude: location.coords.latitude,
          zoom: canPublishAndQuery(location) ? 14 : 12,
          bearing: 0,
          pitch: 0,
        });

        // P2-48 — from here there is a position on the map and a finished
        // discovery pass behind it. A later background watch failure must
        // degrade quietly instead of covering all of that with a card.
        mapHasUsableDataRef.current = true;
      }

      setLocationLoading(false);
    } catch (error) {
      console.error('Error fetching location:', error);
      setLocationLoading(false);

      if (error?.code === 'PERMISSION_DENIED') {
        setLocationOff(true);
        setLocationDenied(true);
      } else {
        setLocationError(error.message || 'Failed to get your location');
      }

      setAllUsers([]);

      setViewport({
        longitude: 34.7818,
        latitude: 32.0853,
        zoom: 12,
        bearing: 0,
        pitch: 0,
      });
    } finally {
      locationLoadInFlightRef.current = false;
    }
  };

  // Mission state drives the empty-map explanation. Failure leaves it null and
  // the generic copy stands in, so the map never blocks on it.
  useEffect(() => {
    let cancelled = false;
    fetchFirstTradeMission()
      .then((m) => { if (!cancelled) setMission(m); })
      .catch((e) => {
        if (cancelled) return;
        setMission(null);
        // fetch rejects with a TypeError only when no answer came back at all.
        // A visit that reused its searches sent no other request (B-27).
        if (e instanceof TypeError) setServerUnreachable(true);
      });
    return () => { cancelled = true; };
  }, []);

  // Where collectors ARE, fetched only once the map has established there are
  // none here. Never fetched eagerly: it is a second discovery-rate-limited
  // request, and on the common path (pins on screen) its answer is unused.
  const [hotspots, setHotspots] = useState([]);

  // Subscribed, not read once: an admin flipping the beta kill switch must
  // take effect in an open session, not only after the next cold start.
  const roamingEnabled = useRoamingEnabled();

  const emptyReason = useMemo(
    () => emptyMapReason(mission, discoveryStatus),
    // `roamingEnabled` is not an argument — emptyMapReason reads the runtime
    // flag itself — but it IS an input, and without it here the memo would
    // keep serving a `showHotspots` computed before the kill switch moved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mission, discoveryStatus, roamingEnabled]
  );

  /**
   * The same failure copy, for a map that is NOT empty (P1-30).
   *
   * Discovery is two requests over one limiter, and the heavier of the two is
   * the matches query — so "nearby succeeded, matches failed or 429'd" is the
   * common shape, not an edge. `emptyReason` already turned that into honest
   * copy with a retry, but its only render site is the empty-state badge, gated
   * on `allUsers.length === 0`. A populated map therefore showed a neighbourhood
   * full of collectors, zero trade opportunities, no callout, no explanation and
   * no way to retry: the "a failed request is unknown, not zero" rule (#82)
   * honoured in the state layer and lost in the render layer.
   *
   * Deliberately derived from `discoveryStatus` alone, with a null mission:
   * `emptyMapReason` lets account state ("Nothing to match on yet") outrank a
   * failed request, which is right for the empty-map badge — that CTA is what
   * the user should act on either way — but it would mean a not-matchable user
   * on a populated map never learns the request failed. Passing null skips that
   * branch and leaves the two `DISCOVERY_*` branches, whose wording (including
   * the 429's) is reused verbatim. `DISCOVERY_OK` yields null.
   */
  const discoveryFailureNotice = useMemo(
    () => emptyMapReason(null, discoveryStatus),
    [discoveryStatus]
  );
  /**
   * The preview's card strip.
   *
   * Fetched here rather than inside the sheet because MapPage already owns the
   * selection, and because the strip has to survive the sheet's own re-renders
   * while a drag is in flight. `glory` leads deliberately: the Vault is the part
   * of a collection that exists to be shown off, so it is what a first glance
   * should land on — same reasoning as the Vault showcase inside the full sheet.
   */
  useEffect(() => {
    const id = selectedUser && (selectedUser._id || selectedUser.id);
    if (!previewOpen || !id || !isValidMongoId(String(id))) {
      setPreviewCards([]);
      setPreviewTotal(0);
      setPreviewLoading(false);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    getUserCollection(String(id))
      .then((data) => {
        if (cancelled) return;
        const glory = Array.isArray(data?.glory) ? data.glory : [];
        const trade = Array.isArray(data?.trade) ? data.trade : [];
        const sale = Array.isArray(data?.sale) ? data.sale : [];
        const all = [...glory, ...trade, ...sale];
        setPreviewCards(all);
        setPreviewTotal(all.length);
      })
      .catch(() => {
        if (cancelled) return;
        // A failed collection read is unknown, not empty: show no strip rather
        // than "no public cards", which would be a claim we cannot make.
        // null = unknown, so the sheet can say it failed (code review
        // ERRORS-STATES-6: 0 rendered "No public cards yet.").
        setPreviewCards([]);
        setPreviewTotal(null);
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => { cancelled = true; };
  }, [previewOpen, selectedUser]);

  // Retry after a failed or throttled discovery load (#82): re-run the exact
  // query that failed. Success clears the failure state inside the load.
  const handleRetryDiscovery = useCallback(async () => {
    const last = lastDiscoveryQueryRef.current;
    if (!last) return;
    setLocationLoading(true);
    try {
      await loadDataAtCoordinates(last.lng, last.lat, last.radius);
    } finally {
      setLocationLoading(false);
    }
  }, [loadDataAtCoordinates]);

  // A failed discovery used to stay on screen until "Try again" or a map move:
  // 7 minutes after Wi-Fi came back and the app had resumed, the map still said
  // "We couldn't check for collectors" (tablet QA, versionCode 11). Coming back
  // to the app, or the connection returning, now re-runs the failed query —
  // only a FAILED one, so a healthy map is not re-queried on every resume.
  const discoveryFailedRef = useRef(false);
  discoveryFailedRef.current = discoveryStatus === DISCOVERY_FAILED;
  const retryIfDiscoveryFailed = useCallback(() => {
    if (discoveryFailedRef.current) handleRetryDiscovery();
  }, [handleRetryDiscovery]);
  useAppResume(retryIfDiscoveryFailed);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    window.addEventListener('online', retryIfDiscoveryFailed);
    return () => window.removeEventListener('online', retryIfDiscoveryFailed);
  }, [retryIfDiscoveryFailed]);

  const handleOpenSidebar = () => setSidebarOpen(true);
  const handleCloseSidebar = () => setSidebarOpen(false);

  /**
   * Clear the selection entirely — pin, preview and sheet.
   *
   * Tapping the empty map, or the preview's close button, means the same thing,
   * so it runs through one function. Discovery results and the camera are
   * deliberately untouched: dismissing someone's card is not a reason to
   * re-query the neighbourhood or move the map out from under the user.
   */
  /**
   * Put the selection surfaces away. Does NOT touch a live match session — see
   * `clearSelection` for the gesture that ends one too.
   */
  const closeSelectionSurfaces = useCallback(() => {
    setSelectedUser(null);
    setSelectedSpot(null);
    setPreviewOpen(false);
    setSidebarOpen(false);
  }, []);

  /**
   * Dismiss everything, session included. This is the *background tap* gesture
   * and the two navigations away from the map — not the preview's close button.
   *
   * Why this is split from `closeSelectionSurfaces` at all:
   *
   * The preview sheet's own "Reveal match" CTA starts a trail, and the sheet
   * covered most of it (measured on the rig at 375x812: the trail spanned
   * y297-433 behind a sheet whose top edge was 315). Routing the sheet's close
   * button through HERE cancelled the trail it had just started, taking the
   * pill, all 11 beacons and all 3 trail lines with it. Escape did the same.
   * The feature had no reachable end state from that entrance at all.
   *
   * The sheet no longer waits to be closed — `handleRevealFromPin` stands it
   * down as the journey begins, so one stage owns the bottom slot at a time.
   * The split still matters, and matters more: the reveal entrance calls
   * `closeSelectionSurfaces`, and calling THIS instead would cancel the session
   * in the same tick it started.
   */
  const clearSelection = useCallback(() => {
    closeSelectionSurfaces();
    // A tap on empty map means "dismiss whatever is up", and the match deck is
    // one of the things that can be up. It shares the bottom slot with the
    // preview and sits at Z.mapPanel (1090) against the preview's 35, so
    // leaving it open here made the gesture silently do nothing at all.
    // Read through the ref: the session is declared below this callback.
    if (revealSessionRef.current?.isLive) revealSessionRef.current.cancel();
  }, [closeSelectionSurfaces]);

  /** Compact preview → the full collection sheet, same selection throughout. */
  const handleExpandPreview = useCallback(() => {
    if (!selectedUser) return;
    setPreviewOpen(false);
    setSidebarOpen(true);
    trackEvent('map_collection_opened', { count: 1 });
  }, [selectedUser]);

  const handleOpenSelectedProfile = useCallback(() => {
    const target = selectedUser;
    const id = target && (target._id || target.id);
    if (!id) return;
    trackEvent('map_profile_opened', { count: 1 });
    clearSelection();
    navigate(`${ROUTES.PROFILE}/${id}`);
  }, [selectedUser, clearSelection, navigate]);

  /**
   * Message the selected collector.
   *
   * Mirrors the sidebar's existing route exactly rather than inventing a second
   * one: create-or-reuse the thread, then open chat on it. The fallback matters
   * — `getOrCreateThread` rejects with a plain string, and ChatPage can create
   * the thread itself from a `userId`, so a failure still lands you in a
   * conversation instead of on an error.
   */
  const handleMessageSelected = useCallback(async () => {
    const target = selectedUser;
    const id = target && (target._id || target.id);
    if (!id || messageBusy) return;
    setMessageBusy(true);
    try {
      const thread = await getOrCreateThread(id);
      navigate(`${ROUTES.CHAT}?threadId=${thread._id}`);
    } catch {
      // The chat screen asks again and shows its own answer (B-31).
      claimRefusals();
      navigate(`${ROUTES.CHAT}?userId=${id}`);
    } finally {
      setMessageBusy(false);
    }
  }, [selectedUser, messageBusy, navigate]);

  /**
   * Fit the camera to a cluster's members.
   *
   * No refetch and no republish: a cluster tap is a camera move, and treating it
   * as a new discovery query would both cost a request and let the pins change
   * underneath the gesture that was meant to reveal them.
   */
  const handleClusterOpen = useCallback((cluster) => {
    const bounds = boundsForCluster(cluster);
    const map = mapRef.current;
    if (!bounds || !map || typeof map.fitBounds !== 'function') return;
    try {
      map.fitBounds(bounds, {
        padding: { top: topChromeReservedPx(ROUTES.MAP) + 96, bottom: CHROME_INSET.bottom + 24, left: 48, right: 48 },
        maxZoom: 16,
        duration: prefersReducedMotion() ? 0 : MOTION.recenterCameraMs,
        essential: true,
      });
    } catch {
      // A camera failure degrades to "no move" — the pins are still all there.
    }
  }, []);

  const handleUserClick = useCallback((clickedUser) => {
    // #148: while a Match Reveal owns the screen, a marker interaction must never
    // open the profile drawer. On Android the pin's pointerdown/touchstart/click
    // can fire in a different order than desktop, so the very tap that triggered
    // "Reveal match" can deliver a trailing click to the pin — and without this
    // guard that reopens the profile drawer over (or behind) the reveal, exactly
    // the bug this fixes. The reveal offers "View their collection" as the one
    // intentional route to the profile. Read the ref, not the state, so a
    // trailing event that fires before the reveal commits is still blocked.
    if (matchRevealRef.current) return;
    // The same handler serves the Listings view, where the preview sheet does not
    // exist — `previewVisible` requires `viewMode === 'map'`. Falling through would
    // set an invisible flag and close the one surface that *can* answer the tap, so
    // "View Seller" and "Trade" on a listing would do nothing at all. Off the map,
    // the drawer is still the answer.
    if (viewMode !== 'map') {
      setSelectedSpot(null);
      setSelectedUser(clickedUser);
      setPreviewOpen(false);
      setSidebarOpen(true);
      trackEvent('map_collector_selected', { count: 1 });
      return;
    }
    // Step one of two: the compact preview, not the full sheet. Tapping a face
    // used to throw an 88%-height drawer over the map, which meant you could
    // not look at two collectors without losing your place on the map between
    // them. The preview answers "who is this" in about a third of the screen
    // and escalates to the same sheet on request.
    //
    // A live session stands down first. The deck occupies the same bottom slot
    // and renders 1055 z-levels above the preview, so without this the tap
    // opened the preview *underneath* the deck: the hover popup flashed, the
    // sheet mounted invisibly, and nothing appeared to happen. Only non-match
    // pins reach this handler — a beacon tap goes to onSelectMatch — so tapping
    // a person here is an unambiguous "I am done with the matches".
    if (revealSessionRef.current?.isLive) revealSessionRef.current.cancel();
    setSelectedSpot(null);
    setSelectedUser(clickedUser);
    setPreviewOpen(true);
    setSidebarOpen(false);
    trackEvent('map_collector_selected', { count: 1 });
  }, [viewMode]);

  const handleSafeSpotSelect = useCallback((spot) => {
    if (revealSessionRef.current?.isLive) revealSessionRef.current.cancel();
    setSelectedUser(null);
    setPreviewOpen(false);
    setSidebarOpen(false);
    setSelectedSpot((cur) => (String(cur?._id) === String(spot?._id) ? null : spot));
  }, []);

  useEffect(() => {
    if (!selectedSpot) {
      setSpotTrades(null);
      setSpotTradesError(false);
      setSpotTradesLoading(false);
      return undefined;
    }
    let cancelled = false;
    setSpotTradesLoading(true);
    setSpotTradesError(false);
    getMyTradeRequests()
      .then((data) => { if (!cancelled) setSpotTrades(data); })
      .catch(() => { if (!cancelled) setSpotTradesError(true); })
      .finally(() => { if (!cancelled) setSpotTradesLoading(false); });
    return () => { cancelled = true; };
  }, [selectedSpot]);

  /**
   * The live ranked queue. One ordering — mutual, then actionable cards, then
   * rounded distance, then a stable id — feeds the callout copy, the traced
   * trails, the selected beacon, the deck's "MATCH 2 OF 8" and the analytics
   * rank. Three separate orderings is what let the loud callout describe one
   * trader while the brightest trail pointed at another.
   *
   * Built from the match records rather than from `allUsers`, because those are
   * two different questions. `allUsers` is "who has a place on this map", and it
   * drops anyone without publishable coordinates — so a match whose location is
   * hidden used to vanish from the flow entirely rather than appearing in the
   * queue with "Location approximate". A match is still a match without a dot.
   *
   * The map user is preferred where one exists: `mergeMatches` has already
   * attached the avatar and online state to it.
   */
  const rankedMatches = useMemo(() => {
    if (matchRecords.length === 0) return [];
    const byId = new Map(allUsers.map((u) => [idStr(u.id), u]));
    return rankMatches(
      matchRecords.map((m) => {
        const mapUser = byId.get(idStr(m.id));
        if (!mapUser) return m;
        // Take presentation from the map user (avatar, online state, anything
        // merged in from the nearby-users response) but ALWAYS take the match
        // fields from the record.
        //
        // Preferring the map user wholesale was wrong: when the wishlist-matches
        // request fails while nearby-users succeeds, `mergeMatches` rebuilds
        // `allUsers` from the nearby response alone, which carries no match data
        // — so a trader whose intact match we still hold in `matchRecords` read
        // "0 cards connect your collections" and "New to trading". That is the
        // "a failed request is unknown, not zero" rule (#82) leaking back in.
        return {
          ...mapUser,
          matchedCards: m.matchedCards,
          theyWantCards: m.theyWantCards,
          direction: m.direction,
          verified: m.verified,
          successfulTrades: m.successfulTrades,
          approxDistanceMeters: m.approxDistanceMeters,
        };
      })
    );
  }, [allUsers, matchRecords]);

  // Kept under its old name: it is still "the one match the callout is about",
  // it is just no longer computed by a second, distance-only sort.
  const primaryOpportunity = rankedMatches[0] || null;

  /**
   * The callout's sentence, counted per direction (P1-34).
   *
   * This used to be `opportunityCalloutCopy({ matchCount: matchedUserIds.length,
   * direction: matchDirection(primaryOpportunity) })` — the whole population's
   * count wearing rank 1's direction. Since `rankMatches` sorts `mutual` first,
   * one two-way match made the map's strongest claim ("N traders have a two-way
   * swap with you") apply to N-1 people it was false for; the `they_want` branch
   * was wrong the same way. Direction is a per-trader fact and does not
   * aggregate.
   *
   * So: the headline speaks for ONE direction group with that group's own count,
   * and every other group present is named after it with its own count. The
   * leading group is rank 1's, which keeps the headline pointing at the same
   * trader the CTA and the deck open on — the one ordering everything else
   * reads. A set that shares a single direction produces exactly the sentence it
   * always did, because then the group IS the population.
   *
   * Counted over `rankedMatches`, not `matchedUserIds`: those are the matches we
   * actually hold a direction for. They are the same set in practice (both are
   * built from one response), and where they ever diverge the smaller, known one
   * is the honest number.
   */
  const calloutCopy = useMemo(() => {
    if (!primaryOpportunity) return null;

    const counts = rankedMatches.reduce((acc, m) => {
      const d = matchDirection(m);
      acc[d] = (acc[d] || 0) + 1;
      return acc;
    }, {});

    const lead = matchDirection(primaryOpportunity);
    const headline = opportunityCalloutCopy({
      matchCount: counts[lead] || 0,
      direction: lead,
    });

    // Largest remaining group first — "4 more have cards you want" before
    // "1 more wants a card from your Trade Binder".
    const rest = Object.entries(counts)
      .filter(([direction, n]) => direction !== lead && n > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([direction, n]) =>
        (CALLOUT_TAIL_COPY[direction] || CALLOUT_TAIL_COPY.they_have)(n)
      )
      .join(' · ');

    return { headline, rest };
  }, [primaryOpportunity, rankedMatches]);

  // Open/close the reveal through one door each, so its exclusivity with the
  // profile drawer + marker sheet can't drift apart across call sites (#148).
  const openMatchReveal = useCallback((target) => {
    // The reveal owns the viewport. Set the guard ref synchronously (before React
    // commits) and close any profile drawer a racing marker event may already
    // have opened, so the reveal mounts alone — and, with the render guard on the
    // drawer, stays that way until it is dismissed.
    matchRevealRef.current = target;
    setMatchReveal(target);
    setSidebarOpen(false);
  }, []);

  const closeMatchReveal = useCallback(() => {
    matchRevealRef.current = null;
    setMatchReveal(null);
    // Put the camera back where the reveal found it (#85). Only for a MULTI
    // match queue: that is the case where `closeReveal` preserves `frame`, so
    // the deck returns advertising a framing the camera has left, while the
    // camera sits zoomed on one collector. A lone match ends the session and
    // sets `frame: "none"`, which is a different screen and out of scope here.
    //
    // The fit effect provably cannot do this for us: closing changes none of
    // its six dependencies, so it does not re-run, and its value signature
    // would early-return if it did.
    //
    // EVERY exit restores, including "Build trade" and "View their collection".
    // That is not a preference, it is the only reachable behaviour: the reveal
    // closes ITSELF first on both of those buttons
    // (`MatchRevealMoment.jsx:744`, `:768` call `onClose?.()` before
    // `onTradeRequest`/`onViewProfile`), so by the time MapPage's own handler
    // runs the camera is already back and the stash is already spent. An
    // earlier revision of this fix carried a sentinel to opt those two paths
    // out; it was dead code, and two tests certified a scoping the product does
    // not have. Whether they SHOULD keep the camera is an open question that
    // cannot be answered from here — it needs MatchRevealMoment to stop
    // closing itself. Filed rather than guessed.
    //
    // `lastFitRef` is deliberately NOT cleared. The restore returns the camera
    // to precisely the state that existed when `lastFitRef` was last written,
    // so the invariant it guards is preserved either way. (Note the weaker
    // reason is the true one: the pre-reveal camera is NOT necessarily the fit
    // effect's output — the user may have panned after it — but the restore
    // still puts back whatever was there when the signature was stored.)
    // Clearing it would let the next unrelated dependency tick re-fit an
    // unchanged frame, which is the yank the signature exists to prevent.
    const multi = (revealSessionRef.current?.state?.queue?.length || 0) > 1;
    const pre = preRevealViewportRef.current;
    preRevealViewportRef.current = null;
    if (multi && pre) {
      setViewport({
        ...pre,
        // NOTE: react-map-gl v8 has no `transitionDuration` view-state prop, so
        // this is a jump cut, not an ease. Kept for consistency with
        // `buildRevealViewport` and the recenter path, both of which set it the
        // same way; making the camera actually ease is a separate change to all
        // three call sites.
        transitionDuration: prefersReducedMotion() ? 0 : MOTION.recenterCameraMs,
      });
    }
    // Hand the map back its turn: the session marks the match explored and
    // restores the deck at the same queue position (or ends, for a lone match).
    // Read through a ref because the session is declared below this — and it
    // has to be, since it opens the reveal through the handler defined below.
    revealSessionRef.current?.closeReveal();
  }, []);

  const handleRevealMatch = useCallback((trader) => {
    const target = trader || primaryOpportunity;
    if (!target) return;
    // One tap, one reveal. The deck's CTA is a big target and the reveal takes
    // a beat to mount, so it collects double- and triple-taps — and every one
    // of them used to re-enter this handler: three `match_revealed` events for
    // one reveal (measured in the rig), plus a re-set viewport and a restarted
    // foil-spark timer. `match_revealed` is the funnel's payoff metric AND it
    // is allowlisted, so each duplicate also stores another dated
    // viewer -> trader edge for 90 days.
    //
    // Guarding on the ref rather than the state because it is assigned
    // synchronously in `openMatchReveal`, so it is already correct for taps
    // that land in the same tick — which is exactly the case being defended
    // against. Revealing a DIFFERENT match while one is open still proceeds,
    // and re-revealing the same match after closing it is a genuine second
    // reveal (the ref is null by then).
    if (matchRevealRef.current && idStr(matchRevealRef.current.id) === idStr(target.id)) return;
    trackEvent('match_revealed', { traderId: idStr(target.id) });
    // Stash the framing the user was looking at before the reveal takes the
    // camera, so closing the reveal can put it back (#85). `viewportRef` is the
    // committed camera, which tracks the imperative `fitBounds` too: every
    // camera move raises mapbox's `move`, MapView forwards it as
    // `onViewStateChange`, and MapPage writes it straight back into `viewport`.
    // Reading the ref rather than `viewport` keeps this callback off the
    // camera's identity, which changes on every frame of a pan.
    preRevealViewportRef.current = viewportRef.current;
    setViewport((prev) => {
      const nextVp = buildRevealViewport(target, prev);
      return nextVp || prev;
    });
    setFoilSparkId(idStr(target.id));
    if (foilSparkTimerRef.current) clearTimeout(foilSparkTimerRef.current);
    foilSparkTimerRef.current = window.setTimeout(() => setFoilSparkId(null), 480);
    setOpportunityCalloutDismissed(true);
    // Show what they actually have. Opening the profile sheet here instead made
    // the whole mystery build-up pay off with a bio field; the sheet is now one
    // of the choices offered *after* the reveal.
    openMatchReveal(target);
  }, [primaryOpportunity, openMatchReveal]);

  const openTraderProfile = useCallback((trader) => {
    // Intentional route out of the reveal ("View their collection"): close the
    // reveal FIRST so the two are never on screen together, then open the
    // profile. Both updates batch into one commit, so the render guard sees
    // matchReveal cleared and lets the drawer through.
    closeMatchReveal();
    setSelectedUser(trader);
    setSidebarOpen(true);
  }, [closeMatchReveal]);

  const handleRevealTradeRequest = useCallback((trader) => {
    // "Build trade": likewise leave the reveal before the trade modal takes over.
    closeMatchReveal();
    setSelectedUser(trader);
    setTradeTarget(trader);
    setTradePrefill(null);
    setTradeModalOpen(true);
  }, [closeMatchReveal]);

  // ── Match trail session ────────────────────────────────────────────────────
  // MapPage owns the session; every other piece (trails, beacons, deck, flight)
  // is a pure render of its state.
  const revealSession = useRevealSession({
    onOpenReveal: handleRevealMatch,
    // Aggregate only — rank, count and mode. Never a coordinate, a distance, a
    // display name or a card identity.
    onTraceStarted: (payload) => trackEvent('match_trace_started', payload),
    onDeckOpened: (payload) => trackEvent('match_deck_opened', payload),
    onTargetSelected: (payload) => trackEvent('match_target_selected', payload),
  });
  revealSessionRef.current = revealSession;

  const sessionState = revealSession.state;
  const sessionQueue = sessionState.queue;
  const sessionLive = revealSession.isLive;
  const sessionActiveTrader = revealSession.activeTrader;

  /**
   * The session's entrance claims Android Back, because in this one state
   * nothing else does.
   *
   * Back is decided purely by the dismissible stack (`backButton.jsx:136-138`):
   * with nothing registered, `/map` is a home route and the press becomes
   * "Press back again to exit". The session's own surfaces register only once
   * they are ON SCREEN — `MatchDeck` on `deckOpen`, `MatchRevealMoment` on the
   * reveal — and `deckOpen` is not dispatched until ~2060ms after the tap
   * (`motionTokens.js:36` 700 + `:27` 1200 plus `DOCK_DELAY_MS` 160). That is
   * longer than `EXIT_CONFIRM_WINDOW_MS` (2000ms), so a second reflex press
   * quit the app mid-animation.
   *
   * NARROWER THAN "the whole entrance", and each exclusion is a state where
   * something else already owns Back or where taking it would be wrong:
   *
   *  - `viewMode === 'map'` — on the Listings tab the map is `display:none`,
   *    `topSlot` is NONE so the session pill and its X are not rendered, and
   *    nothing cancels the session on a view switch. Claiming Back there would
   *    destroy a session the user cannot see while nothing on screen changed.
   *  - `!(previewOpen && selectedUser)` — the sheet's own STATE, which is not
   *    the same test as "the sheet is on screen". `previewVisible` carries a
   *    `!sessionLive` term of its own, and this condition requires
   *    `sessionLive`, so `previewVisible` is already false wherever this
   *    exclusion is evaluated; reading it as `!previewVisible` would make it a
   *    tautology and hide the case described below, where the state outlives
   *    the mounted sheet. Registering on top of a sheet that IS up would make
   *    Back reach PAST it and delete the trail while leaving the sheet on
   *    screen — undoing the `clearSelection`/`onClose` split, which exists
   *    precisely so that getting the sheet out of the way of the trail does not
   *    delete the trail.
   *
   *    DECIDED (441-commit merge, 2026-09-02; the owner was asked and chose
   *    main's answer). STARTING A TRAIL FROM THE COLLECTOR PREVIEW SHEET CLOSES
   *    THE SHEET. It does not stay up owning Back. Asking for the reveal IS the
   *    handoff — the sheet stands itself down and the journey takes the bottom
   *    slot — which is main's a9dc1d8, "one stage owns the bottom slot":
   *    MatchDeck mounts at Z.mapPanel (1090) against this sheet's 35 and
   *    painted straight over it, and the camera framed beacons into space the
   *    sheet was covering. `handleRevealFromPin` implements it by calling
   *    `closeSelectionSurfaces()` before `startRevealSession(...)`, so from THAT
   *    entrance `previewOpen` and `selectedUser` are both clear by the time the
   *    session is live and this exclusion never fires. Pinned by "closes the
   *    sheet itself, and keeps the trail it just started" and "never lets the
   *    preview and the match deck share the bottom slot" in
   *    MapPage.matchJourneyCancel.test.jsx; the handoff of this registration is
   *    pinned in the same file by "takes Back over from the sheet it stood
   *    down, stranding no registration and letting no press fall through".
   *
   *    THE EXCLUSION IS STILL REACHABLE, and where it fires it is now a hole
   *    rather than a defence. `handleRevealFromPin` is not the only entrance: the
   *    top-slot opportunity callout (`data-testid="reveal-match-callout"`, whose
   *    onClick is `startRevealSession()`) renders inside the `topSlot ===
   *    TOP_SLOT.OPPORTUNITY && !opportunityCalloutDismissed` block, and neither
   *    that condition nor the `topSlot` memo consults `previewOpen` — so the
   *    callout is on screen at the same time as the sheet, and pressing it
   *    starts a session leaving `previewOpen` and
   *    `selectedUser` both set. In that commit `previewVisible` goes false on
   *    its own `!sessionLive` term, so CollectorPreviewSheet unmounts and
   *    releases `collector-preview`, while this exclusion suppresses
   *    `match-session`. Nothing is registered and there is no sheet on screen
   *    for the exclusion to be deferring to. Measured in that state: the stack
   *    is empty, and an armed Back returns EXIT and calls `exitApp` — the app
   *    quits mid-animation, which is the exact defect this registration exists
   *    to prevent, reached through the other door.
   *
   *    LEFT AS IS AND FLAGGED, not fixed here: the exclusion is right whenever
   *    the sheet really is on screen, and the callout entrance is the thing
   *    that leaves `previewOpen`/`selectedUser` set behind a sheet that has
   *    already unmounted. Which of the two to change is an owner call.
   *  - `!deckOpen && !matchReveal` — once either is set, those surfaces own
   *    Back. Standing down keeps the stack to one entry per session, so React
   *    running child effects before the parent's cannot invert the LIFO order.
   *    That matters for the reduced-motion single-match path, where
   *    `MatchRevealMoment`'s close is NOT `cancel`; for the deck it is
   *    belt-and-braces, since `MatchDeck`'s `onDismiss` is `cancel` already.
   *
   * What is left is the case the defect was actually about: a session started
   * from the opportunity callout, on the map tab, with no collector preview
   * open and animations on. The action is the same one the on-screen
   * `cancel-match-session` control runs, so Back and the X agree.
   */
  useDismissible(
    viewMode === 'map' &&
      sessionLive &&
      !sessionState.deckOpen &&
      !matchReveal &&
      !(previewOpen && selectedUser),
    revealSession.cancel,
    'match-session'
  );

  /**
   * Start a session over the CURRENT ranking, then freeze it.
   *
   * Discovery polling keeps writing `matchedUserIds` while a session runs; the
   * session reads only this snapshot, so a refresh mid-entrance cannot reorder
   * the deck under the user's finger or re-point the trail they are watching.
   * Refreshed results apply the next time a session opens.
   */
  const startRevealSession = useCallback((focusId = null) => {
    if (rankedMatches.length === 0) return;
    // One press, one session. `handleRevealFromPin` has always guarded this
    // door (#148); the top-slot CTA did not, so two presses inside one React
    // batch restarted the trace from the top and counted a second
    // `match_trace_started` for a session the user only opened once. Read
    // through the ref because state has not committed yet for taps that land
    // in the same tick — which is exactly the case being defended against.
    if (revealSessionRef.current?.isLive) return;
    setOpportunityCalloutDismissed(true);
    revealSessionRef.current?.start(rankedMatches, {
      focusId: focusId ? idStr(focusId) : null,
      // No map, no entrance to watch: land on the reveal or deck at once (B-36).
      instant: !!mapError,
    });
  }, [rankedMatches, mapError]);

  /**
   * A specific pin's own CTA. Same session, same queue order — it just starts
   * with that trader selected rather than the top-ranked one.
   */
  const handleRevealFromPin = useCallback((trader) => {
    if (!trader) return;
    // A racing marker tap must not start a second session or reach the profile
    // while one already owns the map.
    if (revealSessionRef.current?.isLive || matchRevealRef.current) return;

    const inQueue = rankedMatches.some((t) => idStr(t.id) === idStr(trader.id));
    if (inQueue) {
      // ONE STAGE, ONE SURFACE. The preview is the stage BEFORE match
      // discovery, so asking for the reveal is the moment it hands over.
      //
      // This reverses an earlier decision. The sheet used to stay up and the
      // user closed it themselves to see the trail underneath — see the
      // `clearSelection` note above for why that seemed reasonable at the time:
      // routing this button through `clearSelection` cancelled the very trail
      // it started, so the sheet was left alone rather than risk that. But
      // leaving it meant the deck (Z.mapPanel, 1090) mounted into the same
      // bottom slot as the sheet (35) and painted straight over it, and the
      // camera framed beacons into space the sheet was covering.
      //
      // `closeSelectionSurfaces` is the function that split off for exactly
      // this: it puts the surfaces away and does NOT touch the session. Calling
      // `clearSelection` here would cancel the session in the same tick it
      // began, which is the trap the split exists to prevent.
      closeSelectionSurfaces();
      startRevealSession(idStr(trader.id));
      return;
    }
    // This pin knows about a match the ranked queue does not: discovery hasn't
    // resolved yet, or the match arrived by a route that never wrote
    // `matchedUserIds`. Reveal it directly rather than starting an empty
    // session — an inert primary action is a worse failure than a missing
    // entrance, and this CTA has always promised to show the cards.
    handleRevealMatch(trader);
  }, [rankedMatches, startRevealSession, handleRevealMatch, closeSelectionSurfaces]);

  /** Selecting a beacon during a live session hands over to the deck. */
  const handleSelectBeacon = useCallback((trader) => {
    const index = sessionQueue.findIndex((t) => idStr(t.id) === idStr(trader?.id));
    if (index >= 0) revealSessionRef.current?.selectAt(index);
  }, [sessionQueue]);

  /** The deck's primary action — the only place `match_revealed` fires. */
  const handleDeckReveal = useCallback((trader) => {
    revealSessionRef.current?.openReveal();
    handleRevealMatch(trader);
  }, [handleRevealMatch]);

  /** Beacon state per match id — the map layer renders this, it computes nothing. */
  const beaconStates = useMemo(() => {
    const states = {};
    const animatedIds = new Set(revealSession.animatedIds);
    const arrived = new Set(sessionState.arrived);
    const explored = new Set(sessionState.explored);
    const activeId = revealSession.activeId;

    (sessionLive ? sessionQueue : rankedMatches).forEach((trader, index) => {
      const id = idStr(trader.id);
      const isActive = sessionLive && activeId === id;
      let role = 'idle';
      if (sessionLive) {
        // Post-dock the selection shrinks to an anchor, because the full-size
        // card is now in the deck. One card, one place.
        if (isActive) role = sessionState.docked ? 'anchor' : 'active';
        else role = animatedIds.has(id) ? 'traced' : 'idle';
      }
      states[id] = {
        role,
        arrived: arrived.has(id),
        explored: explored.has(id),
        // Rank 1 is named; the others stay unlabelled until they're selected.
        label: !sessionLive ? '' : index === 0 ? 'Best fit' : isActive ? `#${index + 1}` : '',
      };
    });
    return states;
  }, [
    sessionLive,
    sessionQueue,
    rankedMatches,
    sessionState.arrived,
    sessionState.explored,
    sessionState.docked,
    revealSession.animatedIds,
    revealSession.activeId,
  ]);

  const trailTargets = useMemo(
    () => animatedTargets(sessionQueue, MAX_ANIMATED_TRAILS),
    [sessionQueue]
  );

  // Only a real fix is "you" (code review LOCALE-4): the self dot and every
  // distance line used to be drawn from an IP guess or the default point,
  // which UserSidebar's own contract says must be null.
  const originCoordinates = useMemo(
    () =>
      userLocation?.coords && canPublishAndQuery(userLocation)
        ? {
            longitude: userLocation.coords.longitude,
            latitude: userLocation.coords.latitude,
          }
        : null,
    [userLocation]
  );

  // The Locked-spot picker ranks by nearness when it knows where you are.
  // Memoised because the panel would otherwise see a new array every render,
  // which would restart its debounce before a search could ever fire.
  const privacyProximity = useMemo(
    () =>
      originCoordinates
        ? [originCoordinates.longitude, originCoordinates.latitude]
        : null,
    [originCoordinates]
  );

  const selectedSpotDistanceM = selectedSpot
    ? distanceBetween(privacyProximity, spotLngLat(selectedSpot))
    : null;

  /**
   * One deliberate camera move per frame change — never pin-to-pin flying.
   *
   * Padding is derived from live chrome heights and the deck's MEASURED height,
   * so the framed constellation is reserved out of the deck's space from the
   * first frame and nothing shifts when the deck appears.
   */
  useEffect(() => {
    if (sessionState.frame === 'none' || !originCoordinates) return;
    const map = mapRef.current;
    if (!map || typeof map.fitBounds !== 'function') return;

    const focusOnly =
      sessionState.frame === 'focus' && sessionActiveTrader
        ? [sessionActiveTrader]
        : trailTargets;
    // The selection is always inside the fitted set, so it is kept in view by
    // construction rather than by a "should I refocus?" test.
    const targets =
      sessionActiveTrader && !focusOnly.includes(sessionActiveTrader)
        ? [...focusOnly, sessionActiveTrader]
        : focusOnly;

    const bounds = fitBoundsFor(originCoordinates, targets);
    // No usable bounds: leave the camera exactly where it is. A bad fit is far
    // more disorienting than no move at all.
    if (!bounds) return;

    const reservedBottom =
      CHROME_INSET.bottom + (sessionState.deckOpen ? deckHeight + 24 : 24);
    const padding = {
      // Beacons are anchored bottom-centre, so the top must clear the tallest
      // beacon plus its lift, not a nominal margin. `topChromeReservedPx` takes
      // a PATHNAME — it was previously handed `isMobile`, and the boolean made
      // its `pathname.startsWith(...)` throw inside the try below, which the
      // bare catch swallowed. The camera never moved in production.
      top: topChromeReservedPx(ROUTES.MAP) + 96,
      bottom: reservedBottom,
      left: 48,
      right: 48,
    };

    // Only move the camera when the framing actually changed.
    //
    // `originCoordinates` gets a fresh object identity on every geolocation
    // fix — `watchUserLocation` hands back a new literal each time — so this
    // effect re-runs while the user is panning around their own session. Fitting
    // again on an unchanged frame yanked the map out from under the gesture. The
    // signature compares the inputs by VALUE, so a re-render (or a GPS tick that
    // did not really move) is a no-op.
    const signature = JSON.stringify([bounds, padding, sessionState.frame]);
    if (signature === lastFitRef.current) return;
    lastFitRef.current = signature;

    try {
      map.fitBounds(bounds, {
        padding,
        maxZoom: 15.5,
        duration: prefersReducedMotion() ? 0 : MOTION.matchFrameMs,
        essential: true,
      });
    } catch (err) {
      // A genuine projection/camera failure degrades to "no camera move" — the
      // beacons and the deck still carry the whole interaction. But a bare catch
      // here once hid a plain TypeError for months of review, so make it audible
      // outside production instead of silently doing nothing.
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Match trail: fitBounds failed', err);
      }
    }
  }, [
    sessionState.frame,
    sessionState.deckOpen,
    sessionActiveTrader,
    trailTargets,
    originCoordinates,
    deckHeight,
  ]);

  /**
   * Launch the card's flight from the map beacon into the deck slot.
   *
   * Both rects are measured off live DOM at the moment the flight starts, so a
   * camera move or a viewport change can't strand the clone at a stale target.
   */
  useEffect(() => {
    if (sessionState.phase !== 'docking') {
      setDockFlight(null);
      return;
    }
    const container = mapContainerRef.current;
    const slot = deckCardSlotRef.current;
    const beacon = activeBeaconRef.current;
    if (!container || !slot) return;

    const base = container.getBoundingClientRect();

    // The destination is measured by LAYOUT offset, not by bounding rect.
    //
    // This effect runs on the same commit that mounts the deck, and the deck is
    // mid-`matchDeckUp` — a `translateY(116%)` entrance. `getBoundingClientRect`
    // includes that transform, so the slot reads ~200px below where it will
    // actually settle, and the card flew to the clipped bottom edge of the map
    // and then teleported into the slot. Offsets are transform-independent, so
    // this is where the slot WILL be when the flight lands.
    const toRect = {
      ...offsetWithin(slot, container),
      width: slot.offsetWidth || DECK_CARD_W,
      height: slot.offsetHeight || DECK_CARD_H,
    };

    // A match with no map point has no beacon to fly from; it simply appears in
    // the slot rather than flying from a fabricated position.
    if (!beacon) {
      setDockFlight(null);
      return;
    }
    const beaconRect = beacon.getBoundingClientRect();
    setDockFlight({
      from: {
        x: beaconRect.left - base.left,
        y: beaconRect.top - base.top,
        width: beaconRect.width,
        height: beaconRect.height,
      },
      to: toRect,
    });
  }, [sessionState.phase]);

  const handleTradeRequest = () => {
    setTradeTarget(selectedUser);
    setTradePrefill(null);
    setTradeModalOpen(true);
  };

  // B-59: Android closed the app while an offer was being built. Reopen the
  // arena on it; the arena restores the cards once both binders load. The draft
  // only survives an exit that was not a close, since every close removes it.
  useEffect(() => {
    // A trade-list link opening the arena on someone else (below) wins this
    // mount, and the saved offer stays in storage for the next one, rather
    // than being opened and silently replaced.
    if (routerLocation.state?.tradeIntent) return;
    const draft = readDraft('offer', authUser?._id);
    if (!draft?.to?._id) return;
    setSelectedUser(draft.to);
    setTradeTarget(draft.to);
    setTradePrefill(null);
    setTradeResume(draft);
    setTradeModalOpen(true);
    // Not keyed on the router state: the intent effect clears it right after
    // opening, and re-running here would then open the old draft on top.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?._id]);

  // A public trade-list link (docs/PUBLIC_TRADE_LIST.md). TradeListLanding
  // resolved it and sent the collector here with who to trade with, and their
  // copy of the tapped card when there was one. Open the arena on it once, then
  // clear the state so Back or a reload does not open it again.
  useEffect(() => {
    const intent = routerLocation.state?.tradeIntent;
    if (!intent?.to?._id) return;
    setTradeTarget(intent.to);
    setTradePrefill(intent.listing || null);
    setTradeResume(null);
    setTradeModalOpen(true);
    navigate(routerLocation.pathname, { replace: true, state: null });
  }, [routerLocation.state, routerLocation.pathname, navigate]);

  // A listing's Trade button (B-58): straight to the arena with THAT card
  // already requested, rather than to the seller's sheet and an empty arena
  // where the card had to be found again in their binder.
  const handleTradeListing = (seller, listing) => {
    setSelectedUser(seller);
    setTradeTarget(seller);
    setTradePrefill(listing);
    setTradeModalOpen(true);
  };

  const handleBid = (listing) => {
    setBidModalListing(listing);
  };

  // Jump to user's location. No refetch and no republish: recentring is a camera
  // move, and re-querying discovery on it would let the pins change under a
  // gesture whose whole point is "put me back where I was".
  const handleJumpToUserLocation = () => {
    if (userLocation) {
      setViewport({
        ...viewport,
        longitude: userLocation.coords.longitude,
        latitude: userLocation.coords.latitude,
        // The same refusal the rest of this file makes. Only a fix the browser
        // itself reported may be framed at street level; an IP or default-centre
        // point shown at zoom 16 with the "you are here" dot on it reads as a
        // precision we do not have (see the blocked zoom at :848 and the initial
        // load's `canPublishAndQuery(location) ? 14 : 12` at :995).
        zoom: canPublishAndQuery(userLocation) ? 16 : 12,
        transitionDuration: prefersReducedMotion() ? 0 : MOTION.recenterCameraMs,
      });
      raiseLocationAlert();
      setRecentering(true);
      if (recenterTimerRef.current) clearTimeout(recenterTimerRef.current);
      recenterTimerRef.current = setTimeout(
        () => setRecentering(false),
        MOTION.recenterCameraMs + 60
      );
    }
  };
  
  // Toggle location tracking
  const toggleLocationTracking = () => {
    setLocationTrackingEnabled(prev => !prev);
    if (!locationTrackingEnabled && userLocation) {
      // If turning on tracking, jump to user location
      handleJumpToUserLocation();
    }
  };

  // Jump to user's location and optionally toggle tracking
  const handleLocationControl = () => {
    // If we already have user location, just jump to it
    if (userLocation) {
      handleJumpToUserLocation();
      
      // If user long-presses or double-clicks, toggle tracking
      if (locationControlTimerRef.current) {
        clearTimeout(locationControlTimerRef.current);
        locationControlTimerRef.current = null;
        // Toggle tracking on long press
        toggleLocationTracking();
        return;
      }

      // Set timer for detecting double click/long press
      locationControlTimerRef.current = setTimeout(() => {
        locationControlTimerRef.current = null;
      }, 300);
    } else {
      // If no location yet, try to get it
      loadLocationAndUsers();
    }
    
    // Show feedback
    raiseLocationAlert();
  };

  /**
   * Both location messages share one snackbar slot (P2-48), so dismissing it
   * dismisses both — whichever one the user was actually looking at.
   *
   * MUI passes a reason. 'clickaway' comes from a document-level click listener
   * it installs around every Snackbar, so without this guard the user's next tap
   * anywhere killed the notice, however early — including the degraded warning,
   * which asks for six seconds (#193). Everything else still closes it:
   * 'timeout', 'escapeKeyDown', and the Alert's own X, which MUI wires straight
   * to the close button's onClick and so calls with no reason at all.
   */
  const dismissLocationSnackbar = (_event, reason) => {
    if (reason === 'clickaway') return;
    setLocationWatchDegraded(false);
    setLocationAlert(false);
  };

  /** Same 'clickaway' guard, for the privacy-saved confirmation (#193). */
  const dismissPrivacySaved = (_event, reason) => {
    if (reason === 'clickaway') return;
    setPrivacySaved(false);
  };

  /**
   * Raises the location notice and keys its Snackbar, so a re-raise remounts it.
   * MUI arms the auto-hide timer from an effect whose deps are
   * [open, autoHideDuration, setAutoHideTimer], none of which changes when the
   * same notice is raised again — so without the remount a second recenter would
   * inherit what was left of the first countdown (#193).
   */
  const raiseLocationAlert = () => {
    setLocationAlert(true);
    setLocationNoticeSeq((n) => n + 1);
  };

  // Load location and users data on component mount
  useEffect(() => {
    loadLocationAndUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  /**
   * Every timer this page arms, cleared in one place on unmount.
   *
   * The old cleanup lived on the mount effect and read `locationControlTimer`
   * from state — closing over the mount-time value, which is always null, so it
   * could never clear a live timer. The city-search debounce and the foil-spark
   * timeout were never cleared at all, so navigating away mid-search left a
   * geocoding request and two setState calls pointed at an unmounted tree.
   * Refs, because a cleanup must read the CURRENT handle, not a captured one.
   */
  useEffect(() => {
    return () => {
      if (locationControlTimerRef.current) clearTimeout(locationControlTimerRef.current);
      if (citySearchTimerRef.current) clearTimeout(citySearchTimerRef.current);
      if (foilSparkTimerRef.current) clearTimeout(foilSparkTimerRef.current);
      if (recenterTimerRef.current) clearTimeout(recenterTimerRef.current);
    };
  }, []);
  
  // Setup location watching separately
  useEffect(() => {
    const watchId = watchUserLocation(
      (location) => {
        setUserLocation(location);
        hasRealFixRef.current = canPublishAndQuery(location);
        // P2-48 — a fix arrived, so nothing that said we had lost the user's
        // position is true any more. Without this the failure latched: the
        // success callback only ever set state, never cleared it, so a map
        // that recovered seconds later kept the error card until the user hit
        // Retry or left the page.
        setLocationError(null);
        setLocationWatchDegraded(false);
        watchDegradedAnnouncedRef.current = false;
        // Update viewport if tracking is enabled
        if (locationTrackingEnabled) {
          setViewport(prev => ({
            ...prev,
            longitude: location.coords.longitude,
            latitude: location.coords.latitude
          }));
        }
      },
      async (error) => {
        // Code 1 = permission denied — a decision, not a transient failure.
        // Same terminal state as the initial load: no IP fallback, no retry loop.
        if (error?.code === 1) {
          setLocationOff(true);
          setLocationDenied(true);
          // And the same clearing the initial load's off-branch does, because
          // this is the same terminal state arriving later. Every match we
          // raised is a claim about a neighbourhood we may no longer ask
          // about; the callout that announces one renders in a different slot
          // from the blocked card, so leaving `matchedUserIds` populated put
          // "they have something on your wishlist" and a live Reveal button on
          // screen at the same moment as "Location is off". Clearing the
          // records too, rather than only hiding the callout, so the deck and
          // the reveal session cannot open on them either.
          setMatchedUserIds([]);
          setMatchRecords([]);
          matchedCountRef.current = 0;
          setAllUsers([]);
          // ...and stand the LIVE session down, which clearing the records
          // above does not do. `start()` deliberately freezes its queue so a
          // background poll cannot reorder the deck under a finger — the right
          // call, but it also means an open session holds the only copy of
          // this data that nothing else can reach. So the deck kept naming a
          // nearby collector, printing how far away they were and offering to
          // reveal their cards, directly above the card that says "Your position
          // has not been shared with other collectors" — the exact sentence the
          // clearing above was written to protect, one surface lower.
          //
          // Closing the reveal too: it is a child of the same session and would
          // otherwise be left holding a trader we can no longer justify naming.
          matchRevealRef.current = null;
          setMatchReveal(null);
          revealSessionRef.current?.cancel();
          // And the compact preview, which is the FRONTMOST of all of them and
          // was the one surface this branch missed. `previewVisible` does not
          // consult `locationOff`, so a revoke left a real collector's name,
          // their online state, their trust line and a live Message button
          // sitting on top of the card that says your position has not been
          // shared — the same sentence every clear above was written to protect.
          // Raw setters, to match the rest of this branch and to keep the
          // watch effect's dependency list unchanged.
          setSelectedUser(null);
          setPreviewOpen(false);
          setSidebarOpen(false);
          return;
        }
        // Code 3 = timeout — common indoors with GPS; we fall back without alarming the console
        if (error?.code === 3) {
          console.warn('Location watch timed out, using approximate position if available:', error.message);
        } else if (mapHasUsableDataRef.current) {
          // P2-48 — a background tick failing over a map that is already drawn
          // is a degradation, not a breakage. Keep the diagnostic, drop the
          // alarm; the raw string belongs here and nowhere the user can see.
          console.warn('Location watch failed on a loaded map; keeping the last known position:', error);
        } else {
          console.error('Error watching location:', error);
        }
        // On timeout (code 3), try IP-based fallback for the tracked "my
        // location" viewport only — this never republishes to the server.
        //
        // Never trade a real fix for a guess (LOCALE-4): a watch timeout is
        // common indoors, and the next success refreshes the fix anyway. So
        // when the page already holds a real fix there is nothing an IP
        // estimate could be used for, and ipapi.co is not asked at all (Q-20
        // review L5). It used to be asked and the answer thrown away, which
        // sent the user's IP address to a third party for nothing — and then
        // moved the camera to the guess while keeping the fix. A real fix that
        // has stopped updating is "the map is still showing where you were
        // last", which the degraded notice below says.
        if (error?.code === 3 && !hasRealFixRef.current) {
          try {
            const ipLocation = await getLocationByIP();
            // A real fix may have landed while the lookup was out.
            if (hasRealFixRef.current) return;
            setUserLocation((prev) => (canPublishAndQuery(prev) ? prev : ipLocation));
            // Same reasoning as the success callback: an approximate fix is
            // still a fix, so nothing that said we had lost the user stands.
            setLocationError(null);
            setLocationWatchDegraded(false);
            watchDegradedAnnouncedRef.current = false;
            if (locationTrackingEnabled) {
              setViewport(prev => ({
                ...prev,
                longitude: ipLocation.coords.longitude,
                latitude: ipLocation.coords.latitude
              }));
            }
            return;
          } catch (_) { /* fall through to error */ }
        }

        // P2-48 — everything that reaches here: code 2 (POSITION_UNAVAILABLE —
        // the OS location toggle, airplane mode, a desktop network provider
        // that stopped answering), any code 3 whose IP fallback threw, which
        // is the likely outcome because that fallback needs the same network
        // that just failed, and any code 3 over a real fix (no fallback asked).
        //
        // If the map already has a position and its data, this is one failed
        // background tick, not a map with nothing to show. It used to replace
        // the working map with an opaque, sticky "Location Error" card that no
        // later successful fix could clear. Say it once, quietly, off to the
        // side, and let the next good fix take it away.
        //
        // A timeout over a real fix is the same degradation even before the
        // first search around that fix has answered (`mapHasUsableDataRef` is
        // set only then). The fix stays, so there is something to show, and the
        // card's "We couldn't find your location" would be false over it.
        if (mapHasUsableDataRef.current || (error?.code === 3 && hasRealFixRef.current)) {
          if (!watchDegradedAnnouncedRef.current) {
            watchDegradedAnnouncedRef.current = true;
            setLocationWatchDegraded(true);
            setLocationNoticeSeq((n) => n + 1);
          }
          return;
        }

        // No position and no data — the map genuinely has nothing to draw, so
        // the blocking card is the truth. Our copy, never the browser's own
        // diagnostic string.
        setLocationError(LOCATION_UNAVAILABLE_MESSAGE);
      }
    );
    
    // Store the watch ID in the ref
    locationWatchIdRef.current = watchId;
    
    // Cleanup function to stop watching location
    return () => {
      if (locationWatchIdRef.current) {
        navigator.geolocation.clearWatch(locationWatchIdRef.current);
      }
    };
  }, [locationTrackingEnabled]);

  // Add current user to the map users (fixed infinite loop)
  useEffect(() => {
    if (user && userLocation && allUsers.length > 0) {
      // Check if the user is already in the allUsers array
      const userExists = allUsers.some(u => u.id === user.id);
      
      if (!userExists) {
        const currentUserMarker = {
          id: user.id || 'current-user',
          username: user.name || 'You',
          profilePicture: user.avatar || generatedAvatarUrl(user.name || 'You'),
          coordinates: [userLocation.coords.longitude, userLocation.coords.latitude],
          locationPrecision: 'Exact',
          onlineStatus: 'Online',
          online: true,
          lastActive: new Date().toISOString(),
          bio: 'This is you!',
          collectionStats: {
            totalCards: user.cards || 0,
            cardsForSale: 0,
            completedTrades: user.trades || 0
          },
          cards: []
        };
        
        setAllUsers(prevUsers => [currentUserMarker, ...prevUsers]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, userLocation?.timestamp]); // Only depend on values that indicate actual changes

  const chromeRightPx = topChromeReservedPx(ROUTES.MAP);

  /**
   * ONE top slot, under the top row, shared by every transient message.
   *
   * `chipBandPx` / `overlayTop` / `overlayTopPushed` / `TOAST_PUSH_PX` used to
   * spread six overlays across two moving bands. Now the slot is a fixed offset
   * and the only question is which single message occupies it.
   */
  const slotTop = `calc(max(12px, ${SAFE_TOP}) + ${TOP_ROW_PX}px)`;
  const slotShift = `top ${MOTION.utilityShiftMs}ms ${MOTION_EASING.standard}`;

  const wantsHotspots =
    !!emptyReason?.showHotspots && allUsers.length === 0 && !locationLoading;

  useEffect(() => {
    if (!wantsHotspots) return undefined;
    let cancelled = false;

    (async () => {
      try {
        // Same budget as the searches, so a revisit reuses it too (B-53).
        let labelled = recentHotspots(user.id);
        if (!labelled) {
          const rows = await getCollectorHotspots();
          if (cancelled) return;
          // Label each cell where the Mapbox token already lives. A failed or
          // unavailable lookup resolves to null rather than throwing, so an
          // unnamed hotspot still renders as a usable destination — hiding the
          // row would turn a cosmetic failure into a missing feature.
          labelled = await Promise.all(
            rows.map(async (row) => {
              const name = await reverseGeocode(row.coordinates);
              return {
                key: `${row.coordinates[0]},${row.coordinates[1]}`,
                label: name || 'Somewhere with collectors',
                collectors: row.collectors,
                longitude: row.coordinates[0],
                latitude: row.coordinates[1],
              };
            })
          );
          rememberHotspots(user.id, labelled);
        }
        if (!cancelled) setHotspots(labelled);
      } catch {
        // Silent: this is a supplement to an empty state, never the reason the
        // user is looking at one. An error here must not replace the honest
        // "nobody nearby" card with a failure card.
        if (!cancelled) setHotspots([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [wantsHotspots, user.id]);

  const roamingActive = roamingEnabled && !!roamingLocation;

  /** Collectors on the map, not counting your own marker. */
  const nearbyCount = nearbyCollectorCount(allUsers, user?.id);

  /**
   * Which non-OK state, if any, the map is in — and whether it can be shown as a
   * banner over live pins or has to take the middle of the screen.
   *
   * Copy still comes from `emptyMapReason` and `mapCopy`; only the choice of
   * surface is made here.
   */
  const statusSurface = useMemo(() => {
    if (mapError && !locationError && !locationOff) {
      // B-27: offline, "everything else still works" is false and Listings
      // cannot load either, so say so and offer only Retry. `serverUnreachable`
      // has to carry it in the Android app: the WebView tracks connectivity
      // only with ACCESS_NETWORK_STATE, which the manifest does not declare,
      // so navigator.onLine may never turn false there.
      const offline = serverUnreachable || navigator.onLine === false;
      return {
        variant: 'card',
        testId: 'map-unavailable',
        title: MAP_UNAVAILABLE_TITLE,
        body: offline ? MAP_OFFLINE_MESSAGE : mapError,
        primaryAction: { label: 'Retry', onClick: handleRetryMap, testId: 'map-unavailable-retry' },
        // Listings needs no map tiles. A broken map must not hide the cards
        // nearby collectors are offering one toggle away (device run
        // 2026-09-27: "Map unavailable" over 4 live listings).
        secondaryAction: offline ? null : {
          label: 'Browse listings',
          onClick: () => setViewMode('marketplace'),
          testId: 'map-unavailable-listings',
        },
      };
    }
    if (locationOff) {
      // B-44. In the Android app there is no "site" or "browser". After a
      // second "Don't allow" Android stops showing the prompt, so Try again
      // alone cannot bring location back: name the app's settings page and
      // offer a button that opens it. Only for a refusal, though. With the
      // permission granted and no position (Location switched off, no fix),
      // that page already says Allowed, so point at the phone's Location.
      const inApp = canOpenAppSettings();
      return {
        variant: 'card',
        testId: 'location-off-card',
        iconPath: 'M12 3l7 3v5.5c0 4.2-2.9 7.6-7 8.5-4.1-.9-7-4.3-7-8.5V6l7-3z',
        title: 'Location is off',
        body:
          accountPublished === true
            ? "Your phone isn't sharing its location right now, but the area you shared earlier is still visible to nearby collectors. To hide it, choose Hidden in Location privacy."
            : accountPublished === false
              ? "We can't show nearby collectors or publish your position without your location. Your position has not been shared with other collectors."
              : "We can't show nearby collectors or update your position without your location.",
        caption: !inApp
          ? 'To turn it on, allow location for this site in your browser or device settings, then try again.'
          : locationDenied
            ? 'To turn it on, allow location for Tradeloft in Settings > Apps > Tradeloft > Permissions, then try again.'
            : "To turn it on, check that Location is on in your phone's settings, then try again.",
        primaryAction: { label: 'Try again', onClick: loadLocationAndUsers, testId: 'location-off-retry' },
        secondaryAction: inApp && locationDenied
          ? { label: 'Open settings', onClick: () => openAppSettings(), testId: 'location-off-open-settings' }
          : accountPublished === true
            ? { label: 'Location privacy', onClick: () => setPrivacyOpen(true), testId: 'location-off-privacy' }
            : null,
      };
    }
    if (locationError) {
      return {
        variant: 'card',
        testId: 'location-error-card',
        title: 'Location Error',
        body: locationError,
        primaryAction: { label: 'Retry', onClick: loadLocationAndUsers, testId: 'location-error-retry' },
      };
    }
    if (locationLoading) {
      return {
        variant: 'card',
        testId: 'map-discovery-loading',
        spinner: true,
        title: 'Finding collectors near you',
        body: 'Checking who is around right now.',
      };
    }
    // Pins on screen are still valid, they are just incomplete or stale. A
    // centred card would cover the collectors it is describing.
    //
    // Deliberately `discoveryFailureNotice`, not `emptyReason` (P1-30):
    // `emptyMapReason(mission, …)` answers the mission question FIRST, so a user
    // with no wishlist got "nothing to match on yet" pinned over a map that had
    // just failed to load — a true sentence about the wrong thing. Passing a
    // null mission asks only "what happened to the request".
    if (discoveryStatus !== DISCOVERY_OK && allUsers.length > 0 && discoveryFailureNotice) {
      return {
        variant: 'banner',
        testId: 'map-discovery-failure',
        titleTestId: 'map-discovery-failure-headline',
        title: discoveryFailureNotice.headline,
        body: discoveryFailureNotice.body,
        primaryAction: discoveryFailureNotice.retry
          ? { label: discoveryFailureNotice.retry, onClick: handleRetryDiscovery, testId: 'map-discovery-failure-retry' }
          : null,
        // This banner outranks the roaming bar for the single top slot, and
        // that bar is the ONLY render site of `back-to-my-location`. Recentring
        // moves the camera without clearing `roamingLocation`, so a failed
        // refresh used to remove every way out of a roam: the user was stranded
        // in another city until a retry happened to succeed. The banner carries
        // the exit while it is standing in the bar's place, so the slot still
        // holds exactly one message and the roam is still escapable.
        secondaryAction: roamingActive
          ? { label: 'Back to my location', onClick: handleBackToMyLocation, testId: 'back-to-my-location' }
          : null,
      };
    }
    if (allUsers.length === 0 && emptyReason) {
      const retry = emptyReason.retry
        ? { label: emptyReason.retry, onClick: handleRetryDiscovery, testId: 'map-empty-retry' }
        : null;
      // `open_privacy`: a Hidden collector is asked to change their map
      // visibility, and that dialog lives on this page — open it, do not
      // navigate to the page they are already on.
      const cta = emptyReason.cta && (emptyReason.href || emptyReason.action)
        ? {
            label: emptyReason.cta,
            onClick:
              emptyReason.action === 'open_privacy'
                ? () => setPrivacyOpen(true)
                : () => navigate(emptyReason.href),
            testId: 'map-empty-cta',
          }
        : null;
      // `emptyMapReason` has no notion of roaming, and exactly one of its
      // answers is a sentence about where the USER is standing: `no_collectors`
      // — "No collectors NEARBY yet / nobody IN RANGE right now". While roaming
      // that is the one claim the roaming bar directly above it exists to
      // prevent, and the card is the bigger, centred of the two.
      //
      // Only that branch is rescoped. `not_matchable` and the 429/failed
      // wording are true wherever the camera happens to be, and they still win
      // because `emptyMapReason` ranks them first — swapping inside this branch
      // rather than ahead of it is what keeps that ordering intact.
      // A match with no publishable point is still a match: `rankedMatches` is
      // built from `matchRecords`, not `allUsers`, precisely so a collector
      // whose precision is Hidden still reaches the callout and the reveal. So
      // `allUsers.length === 0` does NOT mean "nobody is in range" — and this
      // card and the opportunity callout occupy different slots, so both
      // rendered and said opposite things about the same neighbourhood.
      // Only `no_collectors` makes that claim; `not_matchable` and the 429 and
      // failure wordings are true whether or not a match exists, so they still
      // show. Same rescoping shape as `browsingEmptyCity` below.
      // Both no_collectors flavours make the same claim about the
      // neighbourhood, so both must yield to a live match.
      if (
        rankedMatches.length > 0 &&
        (emptyReason.reason === 'no_collectors' ||
          emptyReason.reason === 'no_collectors_unknown_mission' ||
          emptyReason.reason === 'no_collectors_not_findable')
      ) {
        return null;
      }
      // Both no_collectors flavours say "nobody in range" — while roaming that
      // range is another city, so both are rescoped (skeptic, launch sprint).
      const browsingEmptyCity =
        roamingActive &&
        (emptyReason.reason === 'no_collectors' ||
          emptyReason.reason === 'no_collectors_not_findable');
      return {
        variant: 'card',
        testId: 'map-empty-card',
        titleTestId: 'map-empty-headline',
        title: browsingEmptyCity
          ? `No collectors in ${roamingLocation.name} yet`
          : emptyReason.headline || 'No collectors nearby yet — be the first.',
        body: browsingEmptyCity
          ? "Nobody here is on the map right now. You're just looking around — your own area is unchanged."
          : emptyReason.body,
        primaryAction: retry || cta,
        secondaryAction: retry ? cta : null,
        // Only on a branch that asked for them, and never while browsing
        // another city — there the user already roamed deliberately, and a
        // list of other cities on top of "No collectors in Berlin yet" would
        // be answering a question they did not ask.
        hotspots:
          emptyReason.showHotspots && !browsingEmptyCity ? hotspots : null,
      };
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mapError,
    serverUnreachable,
    locationError,
    locationOff,
    locationDenied,
    accountPublished,
    locationLoading,
    discoveryStatus,
    allUsers.length,
    rankedMatches.length,
    emptyReason,
    hotspots,
    handleRetryDiscovery,
    handleBackToMyLocation,
    navigate,
    // Exiting a roam can leave `allUsers.length` and `emptyReason` unchanged,
    // so without these the empty card would keep naming a city the user has
    // already left.
    roamingActive,
    roamingLocation?.name,
  ]);

  /**
   * The single occupant of the top slot, by priority.
   *
   * Ranked as data so "only one message at a time" is a property of this list
   * rather than an accident of six independent render guards.
   */
  const topSlot = useMemo(() => {
    if (viewMode !== 'map') return TOP_SLOT.NONE;
    // The new-matches toast draws itself into this slot and owns it for a few
    // seconds; everything else stands down rather than stepping around it.
    if (matchesToastUp) return TOP_SLOT.MATCH_TOAST;
    if (sessionLive && !matchReveal) return TOP_SLOT.SESSION;
    if (statusSurface?.variant === 'banner') return TOP_SLOT.STATUS_BANNER;
    if (roamingActive) return TOP_SLOT.ROAMING;
    if (!locationLoading && primaryOpportunity && matchedUserIds.length > 0) return TOP_SLOT.OPPORTUNITY;
    if (shouldShowNearbyPill({ viewMode, locationLoading, count: nearbyCount, discoveryStatus })) {
      return TOP_SLOT.NEARBY;
    }
    return TOP_SLOT.NONE;
  }, [
    viewMode,
    matchesToastUp,
    sessionLive,
    matchReveal,
    statusSurface,
    roamingActive,
    locationLoading,
    primaryOpportunity,
    matchedUserIds.length,
    nearbyCount,
    discoveryStatus,
  ]);

  /**
   * Is the collector in the preview also a match?
   *
   * Read here rather than passed down from the pin, so the answer is the same
   * whether the preview was opened from the map, a cluster or a deep link.
   */
  const selectedIsMatch = !!selectedUser && isWishlistMatchUser(selectedUser, matchedUserIds);

  /** The compact preview and the collection sheet are one selection, two steps. */
  /**
   * The preview owns the bottom slot only while nothing later in the journey
   * does.
   *
   * `handleRevealFromPin` already stands the sheet down when a session starts,
   * so in the intended flow `previewOpen` is false by the time any of these
   * matter. The terms are here because a surface that shares pixels with three
   * others should not depend on every entrance remembering to be polite: the
   * deck sits at Z.mapPanel (1090) against this sheet's 35 and would silently
   * paint over it, and the trade modal and the drawer both used to let the
   * sheet re-appear behind them on the way out.
   *
   * A guard, not a substitute for the state fix — see the handoff above.
   */
  const previewVisible =
    viewMode === 'map' &&
    previewOpen &&
    !!selectedUser &&
    !matchReveal &&
    !sessionLive &&
    !sessionState.deckOpen &&
    !tradeModalOpen &&
    !sidebarOpen;

  /*
   * P1-30's failure notice is the `banner` variant of `statusSurface` now, so it
   * takes the single top slot rather than being a second surface the ambient
   * pills stepped down under (`ambientTop` / `DISCOVERY_NOTICE_PUSH_PX`, both
   * gone), and its exclusivity with the empty-state card is a property of the
   * `statusSurface` ordering rather than two hand-maintained opposite guards.
   *
   * The count pill is the one thing that still shares those pixels, and it has
   * to: `discoveryStatus` is a COMBINED verdict over two requests, so a matches
   * request that failed while `nearby` succeeded marks the whole load failed —
   * and withholding the count then would hide a number that is simply true. The
   * pill states what is on the map; the banner states that something else did
   * not load. Both are honest, and neither says the other's thing.
   */
  const pillVisible =
    topSlot === TOP_SLOT.NEARBY ||
    (topSlot === TOP_SLOT.STATUS_BANNER &&
      shouldShowNearbyPill({ viewMode, locationLoading, count: nearbyCount }));
  /** Under the banner when there is one, in the slot when there is not. */
  const pillTop = topSlot === TOP_SLOT.STATUS_BANNER
    ? `calc(${slotTop} + ${STATUS_BANNER_PUSH_PX}px)`
    : slotTop;

  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      // /map is a fullscreen route (see layout/chromeRoutes.js), so there is no
      // 56px header above this box to subtract. Subtracting one anyway left a
      // dead strip under the map and pushed the Mapbox canvas out of its own
      // container on mobile.
      // dvh(100), not '100dvh': a Chrome 90 WebView drops the bare unit and
      // this box falls back to its 300px min-height.
      height: isMobile ? dvh(100) : '100vh',
      minHeight: isMobile ? 300 : '100vh',
      overflow: 'hidden',
    }}>
      {/*
        /map was the ONLY route in the app with no heading at all — /collection
        and /wishlist each render one, so a screen-reader user who moved to the
        map lost the single landmark heading navigation relies on. It cannot be
        a visible heading: the map is full-bleed by design and every pixel of
        the top band is already spoken for. So it is visually hidden, using the
        same clip the match-session live region below uses.

        This is the ONLY thing that announces the route. `document.title` is a
        constant "Tradeloft" across every route, and a client-side navigation
        drops focus to `<body>`, so without this an arriving screen-reader user
        is told nothing whatsoever about where they have landed.
      */}
      <Box
        component="h1"
        data-testid="map-page-heading"
        // '1px', not 1: MUI's system reads a unitless 0–1 as a PERCENTAGE, so
        // `width: 1` renders this 100%x100% — a full-viewport absolutely
        // positioned box over the map. It is harmless today only because
        // `clipPath` removes it from hit-testing too (verified in the browser:
        // five probes across the map all landed on the canvas, never on this).
        // That is a trap for whoever edits the clip next, so state real pixels.
        sx={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clipPath: 'inset(50%)', m: 0 }}
      >
        Collector map
      </Box>

      {/* Top row — Map / Listings, and city search where roaming is enabled.
          `Cards` left this row: it was a route change dressed as a map mode, and
          it now lives in the top-right grid button TopAccountBar renders. */}
      {!locationLoading && (
        <MapDiscoveryHeader
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          rightInsetPx={chromeRightPx}
          widthPx={viewportWidth}
          roamingEnabled={roamingEnabled}
          citySearchOpen={citySearchOpen}
          cityQuery={cityQuery}
          cityResults={cityResults}
          onOpenCitySearch={() => { setCitySearchOpen(true); clearSelection(); }}
          onCloseCitySearch={() => { setCitySearchOpen(false); setCityQuery(''); setCityResults([]); }}
          onCityQueryChange={handleCityQueryChange}
          onSelectCity={(city) => { trackEvent('map_city_search_used', { count: 1 }); handleSelectCity(city); }}
        />
      )}

      {/* New-match toast — owns the top slot outright while it is up. */}
      {viewMode === 'map' && (
        <NewMatchesToast
          topOffset={slotTop}
          onVisibilityChange={setMatchesToastUp}
          onClick={() => setOpportunityCalloutDismissed(false)}
        />
      )}

      {/* Session status — the one floating message while a session runs.
          Loud (with a Cancel) during the entrance, quiet once settled. */}
      {topSlot === TOP_SLOT.SESSION && (
        <Paper
          elevation={0}
          data-testid="match-session-pill"
          sx={{
            position: 'absolute',
            top: slotTop,
            transition: slotShift,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 11,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            height: revealSession.isSettled ? 28 : 36,
            pl: 1.5,
            pr: 0.6,
            borderRadius: '18px',
            background: revealSession.isSettled ? 'rgba(255,255,255,0.94)' : 'rgba(19,14,38,0.9)',
            color: revealSession.isSettled ? '#3f3a52' : '#fff',
            backdropFilter: 'blur(12px)',
            boxShadow: '0 10px 26px -8px rgba(19,14,38,0.5)',
            whiteSpace: 'nowrap',
          }}
        >
          <Typography variant="caption" fontWeight={700} sx={{ fontSize: 11.5 }}>
            {sessionStatusCopy(sessionQueue.length, revealSession.animatedIds.length)}
          </Typography>
          <IconButton
            size="small"
            aria-label="Close match session"
            data-testid="cancel-match-session"
            onClick={() => revealSession.cancel()}
            sx={{
              color: 'inherit',
              width: 24,
              height: 24,
              // This is the only way OUT of a match session for a touch user —
              // Escape covers the keyboard, and a finger has no Escape. Measured
              // at 24x24 in the running app, against a 44x44 minimum.
              //
              // The visual stays 24px because the pill that holds it is only
              // 28px tall; growing the button would grow the pill. So the HIT
              // box is expanded instead, with a transparent centred ::after.
              // It reaches ~5px past the pill's right edge and ~6px into the
              // Map/Listings row above, but both of those are empty container
              // area at that x — the chips themselves sit far to the left — so
              // nothing else loses hit area to it.
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 44,
                height: 44,
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Paper>
      )}

      {/* Roaming — names the city, and says plainly that you have not moved. */}
      {topSlot === TOP_SLOT.ROAMING && (
        <MapRoamingBar
          cityName={roamingLocation.name}
          subtitle={
            nearbyCount > 0
              ? `${nearbyCount} collector${nearbyCount === 1 ? '' : 's'} here · your location is unchanged`
              : 'Your location is unchanged'
          }
          onExit={handleBackToMyLocation}
          top={slotTop}
          transition={slotShift}
        />
      )}

      {/* Collectors nearby — one calm count, and only when it is true. The pill
          is withheld entirely on a failed or throttled load rather than
          reporting 0, and it no longer counts your own marker. */}
      {pillVisible && (
        <NearbyCollectorsStatus count={nearbyCount} top={pillTop} transition={slotShift} />
      )}

      {/* Single trade-opportunity callout — mystery copy, no card names.
          Kept on this route deliberately: the map no longer disguises a matched
          collector's pin as a mystery card, so this is the surface that says a
          match exists, and the trail session is reached from here. */}
      {topSlot === TOP_SLOT.OPPORTUNITY && !opportunityCalloutDismissed && (
        <Zoom in={true} style={{ transitionDelay: '650ms' }}>
          <Paper
            elevation={0}
            data-testid="trade-opportunity-callout"
            className="trade-opportunity-callout"
            sx={{
              position: 'absolute',
              top: slotTop,
              transition: slotShift,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 10,
              background: 'rgba(255,255,255,0.96)',
              color: '#0f172a',
              px: 2,
              py: 1.2,
              borderRadius: '16px',
              boxShadow: '0 8px 28px rgba(109,40,217,0.22)',
              border: '1px solid rgba(109,40,217,0.2)',
              maxWidth: 'min(320px, 88%)',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="caption"
              fontWeight={700}
              data-testid="trade-opportunity-headline"
              sx={{ display: 'block', mb: calloutCopy?.rest ? 0.2 : 0.8, lineHeight: 1.35 }}
            >
              {/* Direction comes from the match now. It was pinned to
                  'they_have' because that was the only half the backend
                  computed, so a collector whose binder someone wanted was told
                  the opposite of what was true. And the count is the count of
                  the direction group being spoken about, not the population —
                  see calloutCopy (P1-34). */}
              {calloutCopy?.headline}
            </Typography>
            {calloutCopy?.rest && (
              <Typography
                variant="caption"
                data-testid="trade-opportunity-more"
                sx={{ display: 'block', mb: 0.8, lineHeight: 1.35, opacity: 0.72 }}
              >
                {calloutCopy.rest}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              <button
                type="button"
                data-testid="reveal-match-callout"
                // The CTA opens a trail session rather than jumping straight to
                // the reveal. For a single match the session still lands on the
                // reveal — the label promises the same thing it always did — but
                // two or more now get the traced overview and the deck, which is
                // what the count in the label was already implying.
                onClick={() => startRevealSession()}
                style={{
                  flex: 1,
                  border: 'none',
                  borderRadius: 10,
                  padding: '8px 12px',
                  background: '#6d28d9',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 12,
                  cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                {ctaLabelFor(rankedMatches.length) || 'Reveal match'}
              </button>
              <button
                type="button"
                aria-label="Dismiss opportunity callout"
                data-testid="dismiss-opportunity-callout"
                onClick={() => setOpportunityCalloutDismissed(true)}
                style={{
                  border: '1px solid rgba(15,23,42,0.12)',
                  borderRadius: 10,
                  padding: '8px 10px',
                  background: '#fff',
                  color: '#64748b',
                  fontWeight: 600,
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                Later
              </button>
            </Box>
          </Paper>
        </Zoom>
      )}

      {/* Compact match count when the callout is dismissed but matches remain. */}
      {topSlot === TOP_SLOT.OPPORTUNITY && opportunityCalloutDismissed && (
        <Zoom in={true}>
          {/* This badge is the only way back to the callout until the next
              matches load clears the flag, so it has to be a real control: as a
              Paper it was a div with an onClick — no Tab stop, no Enter, and a
              28px target. Same shape as MatchDeck's frame toggle: a transparent
              44px button, the pill inside carrying the visual, so nothing about
              the design moves. Its text is its accessible name. */}
          <Box
            component="button"
            type="button"
            data-testid="trade-opportunity-badge"
            onClick={() => setOpportunityCalloutDismissed(false)}
            sx={{
              position: 'absolute',
              top: slotTop,
              transition: slotShift,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 9,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 44,
              padding: 0,
              border: 0,
              background: 'transparent',
              cursor: 'pointer',
              font: 'inherit',
              color: 'inherit',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.6,
                background: '#6d28d9',
                color: 'white',
                px: 2,
                py: 0.5,
                borderRadius: '24px',
                boxShadow: '0 4px 14px rgba(109,40,217,0.3)',
              }}
            >
              <FavoriteBorderIcon sx={{ fontSize: 15 }} />
              <Typography variant="caption" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
                {matchedUserIds.length} trade opportunit{matchedUserIds.length === 1 ? 'y' : 'ies'}
              </Typography>
            </Box>
          </Box>
        </Zoom>
      )}

      {/* Marketplace. The top padding clears the whole top row, the account
          buttons included (they are 40px tall at the same top edge), so the
          content needs no right inset (B-45). Reserving the buttons' width
          down the full height left the right third of a phone empty. */}
      {viewMode === 'marketplace' && (
        <Box sx={{
          width: '100%', height: '100%', overflow: 'auto',
          pt: `calc(max(12px, ${SAFE_TOP}) + ${TOP_ROW_PX + 8}px)`,
        }}>
          {/* `isActive` is redundant with the mount guard above and is passed
              anyway (P1-41): the tab's discovery requests come out of the same
              budget as the map's, and this block sits one refactor away from
              the display-toggle its sibling map container uses. The component
              should not need its parent's cooperation to stay quiet. */}
          <MarketplaceView userLocation={userLocation} onUserClick={handleUserClick} onTradeListing={handleTradeListing} onBidListing={handleBid} isRoaming={!!roamingLocation} roamingCity={roamingLocation} isActive={viewMode === 'marketplace'} />
        </Box>
      )}

      {/* Map container - explicit height on mobile for Mapbox GL canvas.
          The height must match the page box above, not subtract a 56px header:
          /map is fullscreen and has none. This box is `mapContainerRef`, the
          offset parent every other map surface is measured against, so a short
          height here lifts the utility stack, the preview sheet and the dock
          off the bottom of the viewport by the same amount. */}
      <Box
        ref={mapContainerRef}
        data-testid="map-canvas-container"
        sx={{
          width: '100%',
          height: isMobile ? dvh(100) : '100%',
          minHeight: 300,
          overflow: 'hidden',
          // The deck and the card's dock flight are positioned against this box
          // and measured against its rect, so it has to be their offset parent.
          position: 'relative',
          display: viewMode === 'map' ? 'block' : 'none'
        }}
      >
        {/* Until we know where you are there is nothing honest to show, so
            show nothing rather than a default city. Opaque, and no spinner of
            its own — the status card above is already saying what is
            happening, and two loaders on one screen was why the veil below
            waits for location. */}
        {locationLoading && (
          <Box
            data-testid="map-position-backdrop"
            sx={{ position: 'absolute', inset: 0, bgcolor: '#f5f2e9', zIndex: 9 }}
          />
        )}

        {/* Map loading state — only once location has resolved, so the two
            loaders never stack on the same pixels. */}
        {!locationLoading && !locationError && !locationOff && mapLoading && (
          <Box sx={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            bgcolor: 'rgba(245,242,233,0.8)', zIndex: 10,
          }}>
            <Loader context="discovery" label="Loading map" />
            <Typography variant="body1" sx={{ mt: 2, color: '#5D4037' }}>Loading map...</Typography>
          </Box>
        )}

        {/* Empty-state badge, and every other state the map can be in, are one
            surface now — a centred card when there is nothing truthful to draw,
            a top banner when the pins are still valid. It never renders over a
            location card because !locationError && !locationOff is part of
            choosing which state applies (see statusSurface above); the loading,
            location-off, location-error and map-unavailable branches are
            ordered, not stacked. */}
        {statusSurface && statusSurface.variant === 'card' && (
          <MapStatusSurface
            variant="card"
            testId={statusSurface.testId}
            titleTestId={statusSurface.titleTestId}
            title={statusSurface.title}
            body={statusSurface.body}
            caption={statusSurface.caption}
            spinner={statusSurface.spinner}
            iconPath={statusSurface.iconPath}
            primaryAction={statusSurface.primaryAction}
            secondaryAction={statusSurface.secondaryAction}
            hotspots={
              statusSurface.hotspots
                ? statusSurface.hotspots.map((spot) => ({
                    ...spot,
                    onSelect: () => handleSelectCity({
                      name: spot.label,
                      longitude: spot.longitude,
                      latitude: spot.latitude,
                    }),
                  }))
                : null
            }
          />
        )}

        {topSlot === TOP_SLOT.STATUS_BANNER && statusSurface && (
          <MapStatusSurface
            variant="banner"
            testId={statusSurface.testId}
            titleTestId={statusSurface.titleTestId}
            title={statusSurface.title}
            body={statusSurface.body}
            primaryAction={statusSurface.primaryAction}
            secondaryAction={statusSurface.secondaryAction}
            top={slotTop}
            transition={slotShift}
          />
        )}

        {/* Privacy above recenter, bottom-anchored above the nav — and stepping
            up out of the collector preview's way rather than sitting under it. */}
        <MapUtilityControls
          bottomPx={
            previewVisible
              ? CHROME_INSET.bottom + 12 + Math.max(previewHeight, PREVIEW_SHEET_PX) + 12
              : selectedSpot
              ? CHROME_INSET.bottom + 12 + 280
              : CHROME_INSET.bottom + 12
          }
          onOpenPrivacy={() => setPrivacyOpen(true)}
          onRecenter={() => { trackEvent('map_recenter_used', { count: 1 }); handleLocationControl(); }}
          recentering={recentering}
          tracking={locationTrackingEnabled}
        />
        <Suspense
          // Once the map has failed, the card says so. A veil left up under it
          // covers the match callout and takes its taps (B-36). This one shows
          // only while MapView's code downloads; MapView's own veil, the one
          // seen on the device, is dropped through its `unavailable` prop.
          fallback={mapError ? null : (
            <Box sx={{
              position: 'absolute', inset: 0,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              bgcolor: 'rgba(245,242,233,0.8)', zIndex: 10,
            }}>
              <Loader context="discovery" label="Loading map" />
              <Typography variant="body1" sx={{ mt: 2, color: '#5D4037' }}>Loading map...</Typography>
            </Box>
          )}
        >
          <MapView
            viewport={viewport}
            users={allUsers}
            onUserClick={handleUserClick}
            selectedUserId={selectedUser?.id ?? null}
            onBackgroundTap={clearSelection}
            onClusterOpen={handleClusterOpen}
            mapRef={mapRef}
            onLoad={handleMapLoad}
            onError={handleMapError}
            unavailable={!!mapError}
            currentUser={user}
            // The memo, not a fresh literal rebuilt on every MapPage render:
            // one source of the viewer's point in this file, and a stable
            // identity so MapView is not re-rendered by an object that did not
            // change. Same value the collector sheet gets below.
            originCoordinates={originCoordinates}
            safeSpots={safeSpots}
            selectedSafeSpotId={selectedSpot?._id ?? null}
            onSafeSpotSelect={handleSafeSpotSelect}
            matchedUserIds={matchedUserIds}
            primaryOpportunityId={primaryOpportunity?.id}
            foilSparkId={foilSparkId}
            onRevealMatch={handleRevealFromPin}
            trailMode={
              sessionLive ? (revealSession.isSettled ? 'selected' : 'discovering') : 'hidden'
            }
            trailTargets={trailTargets}
            beaconStates={beaconStates}
            activeTraderId={revealSession.activeId}
            arrivedIds={sessionState.arrived}
            sessionActive={sessionLive}
            onSelectMatch={handleSelectBeacon}
            activeBeaconRef={activeBeaconRef}
            onViewStateChange={(nextViewport) => {
              if (nextViewport && nextViewport.viewState) {
                setViewport(nextViewport.viewState);
              }
            }}
          />
        </Suspense>

        {/* A full-map #241b52 wash at 10% used to sit here while trails drew, to
            make "unrelated map content recede". It was written against a trail
            layer that never actually rendered (useMap was broken, so the overlay
            drew nothing), and the first time anyone saw the two together the
            wash read as a purple film over the whole map — dimming the streets
            and faces the map exists to show. The trail is legible on its own. */}

        {/* The mystery card in flight between the map beacon and the deck slot.
            Rendered above the deck but below the reveal, and aria-hidden — to
            assistive tech the card simply is where it lands. */}
        {dockFlight && (
          <MatchDockTransition
            from={dockFlight.from}
            to={dockFlight.to}
            durationMs={MOTION.matchDockFlightMs}
            zIndex={Z.modalContent - 1}
            onDone={() => setDockFlight(null)}
          />
        )}

        {/* The Match Deck owns selection once the primary trail has landed. */}
        <MatchDeck
          // The deck yields to anything that owns the screen above it.
          //
          // `closeMatchReveal` restores `deckOpen` for a multi-match queue, and
          // both intentional exits from the reveal ("View their collection",
          // "Build trade") go through it — so the deck used to come back in the
          // same commit that opened the profile drawer or the trade modal. At
          // Z.modalContent it then painted and took taps ABOVE the MUI Drawer
          // and Dialog, which is the same class of bug as #148.
          open={
            viewMode === 'map' &&
            sessionState.deckOpen &&
            !matchReveal &&
            !sidebarOpen &&
            !tradeModalOpen
          }
          queue={sessionQueue}
          activeIndex={sessionState.activeIndex}
          exploredIds={sessionState.explored}
          // The card materialises in the slot exactly as its flight ends, so the
          // full-size card exists in one place at a time and never in two.
          cardVisible={!dockFlight}
          frame={sessionState.frame}
          cardSlotRef={deckCardSlotRef}
          onHeightChange={setDeckHeight}
          onNext={revealSession.next}
          onPrevious={revealSession.previous}
          onReveal={handleDeckReveal}
          onToggleFrame={() =>
            revealSession.setFrame(sessionState.frame === 'overview' ? 'focus' : 'overview')
          }
          onDismiss={revealSession.cancel}
        />

        {/* Step one of the collector preview. Sits above the bottom nav rather
            than covering it, so the map you were reading is still there behind
            it. Gated on !matchReveal for the same reason the profile drawer is
            (#148): a racing marker event must not slide a sheet under a reveal. */}
        {previewVisible && (
          <CollectorPreviewSheet
            collector={selectedUser}
            cards={previewCards}
            cardsLoading={previewLoading}
            totalCards={previewTotal}
            bottomPx={CHROME_INSET.bottom + 12}
            onClose={() => { trackEvent('map_collector_preview_closed', { count: 1 }); closeSelectionSurfaces(); }}
            onExpand={handleExpandPreview}
            onOpenProfile={handleOpenSelectedProfile}
            onMessage={handleMessageSelected}
            messageBusy={messageBusy}
            onHeightChange={setPreviewHeight}
            isMatch={selectedIsMatch}
            matchCopy={selectedIsMatch ? pinMatchBannerCopy(selectedUser) : ''}
            onRevealMatch={() => handleRevealFromPin(selectedUser)}
          />
        )}

        {selectedSpot && !matchReveal && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: Z.mapPanel,
              pointerEvents: "none",
              "& .ss-sheet": { pointerEvents: "auto" },
            }}
          >
            <SafeSpotSheet
              spot={selectedSpot}
              distanceM={selectedSpotDistanceM}
              myId={user?._id}
              trades={spotTrades}
              tradesLoading={spotTradesLoading}
              tradesError={spotTradesError}
              onClose={() => setSelectedSpot(null)}
              onNavigate={(path) => navigate(path)}
            />
          </Box>
        )}
      </Box>

      {/* One polite live region for the whole session: how many matches were
          found, and which one is selected now. */}
      <Box
        aria-live="polite"
        role="status"
        data-testid="match-session-live-region"
        sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clipPath: 'inset(50%)' }}
      >
        {sessionState.live}
      </Box>

      {/* Mobile-optimized swipeable drawer for user details */}
      {isMobile ? (
        <SwipeableDrawer
          anchor="bottom"
          // #148: the profile drawer can never be open while a Match Reveal is.
          // Even if a racing marker event slips a setSidebarOpen(true) through,
          // the drawer stays closed until the reveal is dismissed.
          open={sidebarOpen && !matchReveal}
          onClose={handleCloseSidebar}
          onOpen={handleOpenSidebar}
          disableSwipeToOpen
          // MUI merges its own { keepMounted: true } for the temporary variant
          // BEFORE the caller's ModalProps, so this prop is the only way to turn
          // it off. Dropping the old explicit keepMounted accomplished nothing by
          // itself: the phone sheet still mounted on every map mount with
          // selectedUser === null, UserSidebar still fell back to currentUser, and
          // the map still read the signed-in user's own collection before anyone
          // had tapped a pin. Only the desktop Drawer was ever really fixed, and
          // jsdom pins isMobile to false, so no test could see it.
          // Covered by MapPage.mobileBranch.test.jsx.
          ModalProps={{ keepMounted: false }}
          PaperProps={{
            sx: {
              width: '100%',
              // Taller sheet (#148 follow-up): the profile is card-first now, so
              // give the grid real room instead of a cut-off single row.
              height: '88%',
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              boxShadow: 12,
              overflow: 'visible',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 8,
                left: '50%',
                width: 40,
                height: 5,
                backgroundColor: theme.palette.grey[300],
                borderRadius: 4,
                transform: 'translateX(-50%)',
              }
            }
          }}
          // Not keepMounted — see the ModalProps above, which is what actually
          // switches it off. Because UserSidebar falls back to `currentUser`
          // when `selectedUser` is null, a mounted-but-closed sheet made the map
          // fetch the signed-in user's own collection on every mount, before
          // anyone had tapped a pin.
        >
          <UserSidebar
            currentUser={user}
            selectedUser={selectedUser}
            // P2-46 — `user` is an identity object with no coordinates, so the
            // sheet's distance line could never render. The viewer's point has
            // been sitting in this memo the whole time; hand it over.
            originCoordinates={originCoordinates}
            onClose={handleCloseSidebar}
            onTradeRequest={handleTradeRequest}
            onBid={handleBid}
          />
        </SwipeableDrawer>
      ) : (
        <Drawer
          anchor="left"
          open={sidebarOpen && !matchReveal}
          onClose={handleCloseSidebar}
          PaperProps={{ sx: { width: 360, padding: 2, paddingTop: 0 } }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', py: 1 }}>
            <IconButton onClick={handleCloseSidebar}>
              <CloseIcon />
            </IconButton>
          </Box>
          <UserSidebar
            currentUser={user}
            selectedUser={selectedUser}
            originCoordinates={originCoordinates}
            onClose={handleCloseSidebar}
            onTradeRequest={handleTradeRequest}
            onBid={handleBid}
          />
        </Drawer>
      )}

      {/* Each split dialog has its own boundary, so one still loading never
          holds back another; all four are closed when the map first paints. */}
      <Suspense fallback={null}>
      <TradeRequestModal
        open={tradeModalOpen}
        onClose={() => {
          setTradeModalOpen(false);
          setTradeResume(null);
          setTradeTarget(null);
        }}
        mode="create"
        targetUser={tradeTarget}
        requestedCard={tradePrefill}
        resume={tradeResume}
        onSuccess={(sent) => {
          setTradeModalOpen(false);
          setTradeResume(null);
          setTradeTarget(null);
          // Owned here, not in the modal: the modal unmounts itself on success,
          // which would take the celebration down with it. `sent` is the
          // builder's snapshot of what just left — an empty object keeps the
          // moment openable if a caller ever omits it.
          setTradeProposed(sent || {});
        }}
      />
      </Suspense>

      <Suspense fallback={null}>
      <TradeProposedMoment
        open={!!tradeProposed}
        onClose={() => setTradeProposed(null)}
        recipient={tradeProposed?.recipient}
        cards={tradeProposed?.cards}
        cardCount={tradeProposed?.cardCount}
        cash={tradeProposed?.cash}
        expiresAt={tradeProposed?.expiresAt}
        // B-12: why this offer can't be accepted yet, when the server warned.
        subline={tradeProposed?.note}
        // The screen now offers two real exits, so it waits for one rather than
        // closing itself out from under a reaching thumb.
        holdMs={null}
        onViewTrade={() => {
          setTradeProposed(null);
          navigate(ROUTES.TRADES);
        }}
      />
      </Suspense>

      <Suspense fallback={null}>
      <MatchRevealMoment
        open={!!matchReveal}
        trader={matchReveal}
        onClose={closeMatchReveal}
        onTradeRequest={handleRevealTradeRequest}
        onViewProfile={openTraderProfile}
      />
      </Suspense>

      <Suspense fallback={null}>
      <BidModal
        open={!!bidModalListing}
        onClose={() => setBidModalListing(null)}
        card={bidModalListing}
        listingUserId={bidModalListing?.userId?._id || bidModalListing?.userId}
        onSuccess={() => setBidModalListing(null)}
      />
      </Suspense>

      {/* Location Privacy Dialog */}
      <Dialog
        // Named by its own title (E2E audit 7·17 / 8·1): without a DialogTitle,
        // MUI's aria-labelledby points at nothing and a screen reader says "dialog".
        aria-labelledby="location-privacy-title"
        open={privacyOpen}
        onClose={() => setPrivacyOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '24px', overflow: 'visible',
            boxShadow: '0 24px 60px rgba(107,33,168,0.18)',
            m: 2,
          }
        }}
      >
        <Suspense fallback={<Box sx={{ p: 4 }}><Loader context="panel" label="Loading privacy settings" /></Box>}>
        <LocationPrivacySettings
          onClose={() => setPrivacyOpen(false)}
          // The panel closes itself on a successful save; the confirmation has
          // to be rendered out here or it would unmount with the dialog.
          onSaved={() => {
            setPrivacySaved(true);
            // Same reason as the location notice's key: a second save inside the
            // 3s window has to restart the countdown, not inherit it (#193).
            setPrivacySavedSeq((n) => n + 1);
          }}
          proximity={privacyProximity}
        />
        </Suspense>
      </Dialog>

      {/* Location snackbar — one slot, two messages.
          P2-48 added the degraded one: the watch has stopped delivering fixes
          on a map that already has a position and its data. Deliberately a
          snackbar and not a card, because what is underneath is still correct;
          it has just stopped moving, and the user can carry on browsing.
          It outranks "Showing your current location", which is not true once
          we have stopped getting fixes, and they share this slot. */}
      <Snackbar
        // Namespaced, not the bare counter. This Snackbar and the privacy one
        // below are SIBLINGS in this Box's children, so React reconciles them
        // against each other by key — and two independent counters that both
        // start at 0 collide on every value they happen to share, starting with
        // the very first render. React logs "Encountered two children with the
        // same key" and, in its own words, may duplicate or omit one of them;
        // it duplicated — caught as two MuiSnackbar-root siblings in this Box,
        // both rendering this Alert. The prefix keeps the #193 mechanism exactly —
        // the key still changes on every raise, so the toast still remounts and
        // MUI still re-arms its auto-hide timer — while putting the two
        // sequences in different namespaces.
        key={`location-notice-${locationNoticeSeq}`}
        open={locationWatchDegraded || locationAlert}
        autoHideDuration={locationWatchDegraded ? 6000 : 3000}
        onClose={dismissLocationSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        // The bottom nav is fixed at every breakpoint, so the old sm:24 put this
        // straight on top of it.
        sx={{
          zIndex: Z.snackbar,
          bottom: `calc(${CHROME_INSET.bottom}px + ${SAFE_BOTTOM}) !important`,
        }}
      >
        <Alert
          severity={locationWatchDegraded ? 'warning' : 'info'}
          data-testid={locationWatchDegraded ? 'location-watch-degraded' : 'location-alert'}
          onClose={dismissLocationSnackbar}
        >
          {locationWatchDegraded
            ? LOCATION_WATCH_DEGRADED_MESSAGE
            : 'Showing your current location'}
        </Alert>
      </Snackbar>
      {/* The privacy panel closes itself on a successful save, so its own
          Snackbar would unmount with it. This is that acknowledgement, kept
          out here where it survives the dialog. */}
      <Snackbar
        // Namespaced for the same reason as the location notice above — these
        // two are the colliding pair.
        key={`privacy-saved-${privacySavedSeq}`}
        open={privacySaved}
        autoHideDuration={3000}
        onClose={dismissPrivacySaved}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          zIndex: Z.snackbar,
          bottom: `calc(${CHROME_INSET.bottom}px + ${SAFE_BOTTOM}) !important`,
        }}
      >
        <Alert
          severity="success"
          data-testid="privacy-saved"
          onClose={dismissPrivacySaved}
        >
          Privacy settings saved!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MapPage;

